<template>
  <div class="booking-flow-shell">
    <header-component />
    <main class="booking-flow-shell__main booking-flow-page">
    <Teleport to="body">
      <Transition name="booking-wizard-loading">
        <div
          v-if="wizardLoading"
          class="booking-wizard__loading-overlay"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div class="booking-wizard__loading-panel">
            <BookingWizardLoader size="lg" :color="buttonColor" />
            <p class="booking-wizard__loading-text">{{ wizardLoadingMessage }}</p>
          </div>
        </div>
      </Transition>
    </Teleport>
    <Teleport to="body">
      <button
        v-if="showScrollDownHint"
        type="button"
        class="booking-wizard__scroll-hint"
        :style="scrollHintAccentStyle"
        aria-label="Continue booking"
        @click="scrollToContinueWizard"
      >
        <span class="booking-wizard__scroll-hint-icon" aria-hidden="true">
          <v-icon icon="mdi-chevron-down" size="20" />
        </span>
        <span class="booking-wizard__scroll-hint-label">Continue booking</span>
      </button>
    </Teleport>
    <!-- OLD FLOW (disabled): legacy % progress bar
    <div class="progress">
      <div
        class="progress-bar progress-bar-success"
        role="progressbar"
        aria-valuenow="40"
        aria-valuemin="0"
        aria-valuemax="100"
        v-bind:style="{ width: percentagebar + '%' }"
      >
        {{ percentagebar }}% Complete (success)
      </div>
    </div>
    -->
    <section class="booking-wizard">
      <header class="booking-wizard__intro">
        <h1 class="booking-wizard__title">Make a Booking</h1>
        <p class="booking-wizard__subtitle">
          Complete the steps below to secure your space.
        </p>
      </header>

      <div
        v-if="readOnlyOTP && otpUserBookingHours !== null"
        class="booking-wizard__otp-banner"
        :style="{ '--otp-banner-accent': buttonColor || circleColor }"
      >
        <v-icon icon="mdi-clock-check-outline" size="22" />
        <div class="booking-wizard__otp-banner-copy">
          <strong>OTP hours remaining: {{ otpUserBookingHours }}</strong>
          <p>Complimentary bookings use your pre-approved hours quota.</p>
        </div>
      </div>

      <!-- Step 1: Select Venue -->
      <article
        id="wizard-step-1"
        ref="wizardStep1"
        class="booking-wizard__step"
        :class="{
          'booking-wizard__step--active': wizardStep === 1,
          'booking-wizard__step--completed': wizardStep > 1,
        }"
      >
        <header class="booking-wizard__step-head">
          <span class="booking-wizard__step-badge">1</span>
          <h2 class="booking-wizard__step-title">Select Venue</h2>
        </header>

        <div class="booking-wizard__card booking-wizard__card--venue">
          <div class="booking-wizard__venue-row">
            <label class="booking-search__label" for="venue-select">Venue Name</label>
            <div
              class="booking-search__select-wrap booking-wizard__venue-select"
              :class="{ 'booking-wizard__venue-select--open': venueDropdownOpen }"
            >
          <multiselect
            id="venue-select"
            v-model="selectedVenueName"
            :show-labels="false"
            open-direction="bottom"
            placeholder="Choose a venue to continue"
            :options="venueTypeOptions"
            :searchable="false"
            :allow-empty="false"
            :max-height="260"
            @open="onVenueDropdownOpen"
            @close="onVenueDropdownClose"
          />
            </div>
          </div>

        <div
          v-if="selectedVenueName && (hasDescriptionContent || hasVenueMedia || selectedVenueCapacity)"
          class="booking-wizard__venue-body"
        >
          <div
            class="booking-wizard__venue-grid"
            :class="{ 'booking-wizard__venue-grid--dual-media': hasDualVenueMedia }"
          >
            <div v-if="hasVenueMedia" class="venue-desc__media-section booking-wizard__venue-media">
            <div
              class="venue-desc__media booking-wizard__venue-media-grid"
              :class="{ 'booking-wizard__venue-media-grid--dual': hasDualVenueMedia }"
            >
              <figure v-if="hasVenuePhoto" class="venue-desc__media-item">
                <button
                  type="button"
                  class="venue-desc__media-btn venue-desc__media-btn--frame"
                  aria-label="View venue photo"
                  @click="openvenueImage"
                >
                  <v-img :src="venuePhoto" contain class="venue-desc__media-img" />
                </button>
                <figcaption class="venue-desc__media-caption">Venue photo</figcaption>
              </figure>
              <figure v-if="hasMapPhoto" class="venue-desc__media-item">
                <button
                  type="button"
                  class="venue-desc__media-btn venue-desc__media-btn--frame"
                  aria-label="View location map"
                  @click="openmapImage"
                >
                  <v-img :src="mapPhoto" contain class="venue-desc__media-img" />
                </button>
                <figcaption class="venue-desc__media-caption">Location map</figcaption>
              </figure>
            </div>
            </div>

            <div
              class="booking-wizard__venue-info"
              :class="{ 'booking-wizard__venue-info--dual-media': hasDualVenueMedia }"
            >
              <h3 class="booking-wizard__venue-name">{{ selectedVenueName }}</h3>
              <div v-if="selectedVenueCapacity" class="booking-wizard__meta-row">
                <v-icon icon="mdi-account-group" size="20" color="primary" />
                <p>
                  <strong>Capacity</strong>
                  Up to {{ selectedVenueCapacity }} people
                </p>
              </div>
              <div
                v-if="hasDescriptionContent"
                class="booking-search__details-copy venue-desc"
                v-html="renderedDescription"
              ></div>
            </div>
          </div>
        </div>
        </div>
      </article>

      <v-dialog v-model="showzoomedImage" class="openimage" width="3000">
        <v-row>
          <v-img style="margin-left: 100px" :src="venuePhoto"> </v-img>
          <v-btn
            style="height: 26px; width: 27px"
            icon
            dark
            @click="showzoomedImage = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-row>
      </v-dialog>
      <v-dialog v-model="showmapzoomedImage" class="openimage" width="3000">
        <v-row>
          <v-img style="margin-left: 100px" :src="mapPhoto"> </v-img>
          <v-btn
            style="height: 26px; width: 27px"
            icon
            dark
            @click="showmapzoomedImage = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-row>
      </v-dialog>

      <!-- Step 2: Add Slot -->
      <article
        v-if="wizardStep >= 2"
        id="wizard-step-2"
        ref="wizardStep2"
        class="booking-wizard__step"
        :class="{
          'booking-wizard__step--active': wizardStep === 2,
          'booking-wizard__step--completed': wizardStep > 2,
        }"
      >
        <header class="booking-wizard__step-head">
          <span class="booking-wizard__step-badge">2</span>
          <h2 class="booking-wizard__step-title">Add Slot</h2>
        </header>

        <div class="booking-wizard__card booking-wizard__card--slot">
          <header class="booking-wizard__slot-head">
            <div class="booking-wizard__slot-head-text">
              <h3 class="booking-wizard__slot-title">Select date &amp; time</h3>
              <p v-if="selectedVenueName" class="booking-wizard__slot-subtitle">
                Choose a slot for <strong>{{ selectedVenueName }}</strong>
              </p>
            </div>
          </header>

          <div
            v-if="hasBookingInCart && wizardSessionBooking"
            ref="wizardSessionCard"
            class="booking-session-card"
          >
            <div class="booking-session-card__badge">
              <v-icon icon="mdi-check-circle" size="22" />
              <span>Session added</span>
            </div>
            <div class="booking-session-card__content">
              <h3 class="booking-session-card__venue">{{ wizardSessionBooking.venueName }}</h3>
              <div class="booking-session-card__grid">
                <div class="booking-session-card__item">
                  <span class="booking-session-card__label">Date</span>
                  <span class="booking-session-card__value">{{ formatBookingDate(wizardSessionBooking) }}</span>
                </div>
                <div
                  v-if="!hideTimeForShowgroundFullDay"
                  class="booking-session-card__item"
                >
                  <span class="booking-session-card__label">Time</span>
                  <span class="booking-session-card__value">
                    {{ formatBookingTimeRange(wizardSessionBooking?.StartTime, wizardSessionBooking?.EndTime) }}
                  </span>
                </div>
                <div class="booking-session-card__item">
                  <span class="booking-session-card__label">People</span>
                  <span class="booking-session-card__value">{{ wizardSessionBooking.maxPeople }}</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              class="booking-session-card__change"
              @click="clearWizardSession()"
            >
              Change session
            </button>
          </div>

          <div v-if="showWizardSlotPicker" class="booking-picker booking-picker--inline">
            <div v-if="!overlay" class="booking-picker__body">
              <component
                :is="getComponentName()"
                :selectedvenuename="selectedVenueName"
                :checkValidation="fulldayChecked"
                :fulldayChecked="fulldayChecked"
                :hourlyChecked="hourlyChecked"
                :freebookingChecked="freebookingChecked"
                :otpUserBookingHours="otpUserBookingHours"
                :venuesFromAPI="venuesFromNewAPI"
                :selectedDate="formatDateForAPI(date)"
                :booked-events="events"
                @selectedBookingList="setselectedBookingList"
                @dateSelected="onDateSelected"
                @slot-add-start="onSlotAddStart"
                @slot-add-end="onSlotAddEnd"
              ></component>
            </div>
          </div>

        </div>
      </article>

      <!-- Step 3: Contact Information -->
      <article
        v-if="wizardStep >= 3"
        id="wizard-step-3"
        ref="wizardStep3"
        class="booking-wizard__step"
        :class="{
          'booking-wizard__step--active': wizardStep === 3,
          'booking-wizard__step--completed': wizardStep > 3,
        }"
      >
        <header class="booking-wizard__step-head">
          <span class="booking-wizard__step-badge">3</span>
          <h2 class="booking-wizard__step-title">Contact Information</h2>
        </header>
        <div class="booking-wizard__card booking-wizard__card--contact">
          <booking-contact-step
            ref="contactStep"
            @validity-change="contactStepValid = $event"
          />
        </div>
      </article>

      <!-- Step 4: Additional details / OpenForms (when enabled) -->
      <article
        v-if="showWizardExtraStep && wizardStep >= wizardExtraStep"
        id="wizard-step-extra"
        ref="wizardStepExtra"
        class="booking-wizard__step"
        :class="{
          'booking-wizard__step--active': wizardStep === wizardExtraStep,
          'booking-wizard__step--completed': wizardStep > wizardExtraStep,
        }"
      >
        <header class="booking-wizard__step-head">
          <span class="booking-wizard__step-badge">{{ wizardExtraStep }}</span>
          <h2 class="booking-wizard__step-title">
            {{ showOpenFormsStep ? "Additional Information" : "Booking Details" }}
          </h2>
        </header>
        <div class="booking-wizard__card booking-wizard__card--details">
          <booking-open-forms-step
            v-if="showOpenFormsStep"
            ref="openFormsStep"
            :embeded-url="embededUrl"
            :contact-details="wizardContactDetails"
            @submitted="handleOpenFormsSubmitted"
          />
          <booking-second-detail-step
            v-else-if="showSecondDetailStep"
            ref="secondDetailStep"
            :venue-type="selectedvenueType"
            :button-color="buttonColor"
            :details-settings="detailsSettings"
            :facility-addon-details="facilityAddonDetails"
            :facility-addon-temp="facilityAddonTemp"
            :enable-overlap="enableOverlap"
            :enable-non-overlap="enableNonOverlap"
            :storeaddedbookingdetails="storeaddedbookingdetails"
          />
          <div
            v-if="openFormsProcessing"
            class="booking-wizard__review-processing"
          >
            <BookingWizardLoader
              size="sm"
              :color="buttonColor"
            />
            <span>Preparing your booking summary…</span>
          </div>
        </div>
      </article>

      <!-- Review & Confirm -->
      <article
        v-if="wizardStep >= wizardReviewStep"
        id="wizard-step-review"
        ref="wizardStepReview"
        class="booking-wizard__step"
        :class="{
          'booking-wizard__step--active': wizardStep === wizardReviewStep,
        }"
      >
        <header class="booking-wizard__step-head">
          <span class="booking-wizard__step-badge">{{ wizardReviewStep }}</span>
          <h2 class="booking-wizard__step-title">Review &amp; Confirm</h2>
        </header>
        <div class="booking-wizard__card booking-wizard__card--review">
          <header class="booking-wizard__review-head">
            <h3>Booking summary</h3>
            <p>Please review your details before proceeding to payment.</p>
          </header>

          <section class="booking-wizard__review-section">
            <h4 class="booking-wizard__review-heading">Personal details</h4>
            <dl class="booking-wizard__review-grid">
              <div class="booking-wizard__review-row">
                <dt>Name</dt>
                <dd>{{ wizardContactFullName }}</dd>
              </div>
              <div class="booking-wizard__review-row">
                <dt>Mobile</dt>
                <dd>{{ wizardContactDetails.Phone || "—" }}</dd>
              </div>
              <div class="booking-wizard__review-row">
                <dt>Email</dt>
                <dd>{{ wizardContactDetails.Email || "—" }}</dd>
              </div>
              <div class="booking-wizard__review-row">
                <dt>Address</dt>
                <dd>{{ wizardContactDetails.Address || "—" }}</dd>
              </div>
              <div class="booking-wizard__review-row">
                <dt>Booking name</dt>
                <dd>{{ wizardContactDetails.BookingName || "—" }}</dd>
              </div>
              <div
                v-if="wizardContactDetails.Notes"
                class="booking-wizard__review-row booking-wizard__review-row--full"
              >
                <dt>Booking note</dt>
                <dd>{{ wizardContactDetails.Notes }}</dd>
              </div>
              <template v-if="wizardContactDetails.businessBooking">
                <div class="booking-wizard__review-row">
                  <dt>Company</dt>
                  <dd>{{ wizardContactDetails.companyName || "—" }}</dd>
                </div>
                <div v-if="wizardContactDetails.TradeAs" class="booking-wizard__review-row">
                  <dt>Trading as</dt>
                  <dd>{{ wizardContactDetails.TradeAs }}</dd>
                </div>
                <div class="booking-wizard__review-row">
                  <dt>ABN</dt>
                  <dd>{{ wizardContactDetails.ABN || "—" }}</dd>
                </div>
              </template>
            </dl>
          </section>

          <section class="booking-wizard__review-section">
            <h4 class="booking-wizard__review-heading">Session details</h4>
            <dl class="booking-wizard__review-grid">
              <div class="booking-wizard__review-row">
                <dt>Venue</dt>
                <dd>{{ wizardSessionBooking?.venueName || selectedVenueName }}</dd>
              </div>
              <div class="booking-wizard__review-row">
                <dt>Date</dt>
                <dd>{{ wizardSessionBooking ? formatBookingDate(wizardSessionBooking) : "—" }}</dd>
              </div>
              <div
                v-if="!hideTimeForShowgroundFullDay && wizardSessionBooking"
                class="booking-wizard__review-row"
              >
                <dt>Time</dt>
                <dd>
                  {{ formatBookingTimeRange(wizardSessionBooking?.StartTime, wizardSessionBooking?.EndTime) }}
                </dd>
              </div>
              <div class="booking-wizard__review-row">
                <dt>People</dt>
                <dd>{{ wizardSessionBooking?.maxPeople || "—" }}</dd>
              </div>
              <div class="booking-wizard__review-row">
                <dt>Booking type</dt>
                <dd>{{ wizardBookingTypeLabel }}</dd>
              </div>
            </dl>
            <div class="booking-wizard__review-amount-block">
              <span class="booking-wizard__review-amount-label">Amount</span>
              <span
                v-if="wizardPricingLoading"
                class="booking-wizard__review-amount"
              >
                Calculating…
              </span>
              <span
                v-else-if="freebookingChecked || readOnlyOTP"
                class="booking-wizard__review-amount booking-wizard__review-amount--free"
              >
                {{ readOnlyOTP ? "Complimentary OTP booking" : "Free" }}
              </span>
              <span
                v-else-if="wizardCalculatedAmount > 0"
                class="booking-wizard__review-amount"
              >
                ${{ formatAmount(wizardCalculatedAmount) }}
              </span>
              <span v-else class="booking-wizard__review-amount">—</span>
            </div>
          </section>

          <section
            v-if="wizardSecondPageDetails.length"
            class="booking-wizard__review-section"
          >
            <h4 class="booking-wizard__review-heading">Additional details</h4>
            <dl class="booking-wizard__review-grid">
              <div
                v-for="(item, idx) in wizardSecondPageDetails"
                :key="'second-detail-' + idx"
                class="booking-wizard__review-row booking-wizard__review-row--full"
              >
                <dt>{{ item.question }}</dt>
                <dd>
                  <template v-if="Array.isArray(item.answer)">
                    {{ item.answer.join(", ") }}
                  </template>
                  <template v-else>{{ item.answer }}</template>
                  <span v-if="item.file" class="booking-wizard__review-doc">
                    (document uploaded)
                  </span>
                </dd>
              </div>
            </dl>
          </section>

          <div v-if="wizardPaymentError" class="booking-wizard__review-error" role="alert">
            {{ wizardPaymentError }}
          </div>

          <div v-if="!freebookingChecked" class="booking-wizard__review-notice">
            <p><strong>A surcharge of 0.75% will be applied for all payments.</strong></p>
            <p>
              Proceeding with payment will direct you to the secure Bpoint
              Payment Gateway page via CommBank.
            </p>
          </div>
        </div>
      </article>

      <footer id="wizard-step-nav" ref="wizardNav" class="booking-wizard__nav">
        <button
          type="button"
          class="booking-wizard__btn booking-wizard__btn--back"
          :style="backButtonStyle"
          :disabled="confirmInProgress || wizardLoading"
          @click="wizardBack"
        >
          Back
        </button>
        <button
          v-if="wizardStep < wizardReviewStep && !(wizardStep === wizardExtraStep && showOpenFormsStep)"
          type="button"
          class="booking-wizard__btn booking-wizard__btn--next booking-theme-btn"
          :style="{ backgroundColor: buttonColor, color: 'var(--booking-button-font-color)' }"
          :disabled="!canProceedWizard || overlay || wizardLoading || openFormsProcessing"
          @click="wizardNext"
        >
          Next
        </button>
        <button
          v-else-if="wizardStep === wizardReviewStep"
          type="button"
          class="booking-wizard__btn booking-wizard__btn--confirm booking-theme-btn"
          :style="{ backgroundColor: buttonColor, color: 'var(--booking-button-font-color)' }"
          :disabled="confirmInProgress || wizardLoading || overlay || !hasBookingInCart"
          @click="confirmAndPay"
        >
          <span v-if="confirmInProgress || wizardLoading">Processing…</span>
          <span v-else>{{ freebookingChecked ? "Confirm Booking" : "Confirm and Pay" }}</span>
        </button>
      </footer>
    </section>

    <div class="booking-wizard__section-divider" aria-hidden="true">
      <span class="booking-wizard__section-divider-line"></span>
    </div>

      <!-- OLD FLOW (disabled): legacy multi-booking / calendar-picker checkout UI -->
      <div v-if="false" class="booking-search__legacy">
      <v-card
        variant="outlined"
        v-if="openmultibookingbox == true"
      >
        <v-row class="row_top_padding">
          <!-- First Col -->
          <v-col
            sm="4"
            lg="2"
            md="2"
            class="column"
            style="background-color: #aaa"
          >
            <v-row class="container">
              <form class="first_form">
                <v-col class="form-check">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="radio1"
                    name="optradio"
                    value="option1"
                    checked
                    @click="Openmonthbookingcard(0)"
                  />
                  <label
                    class="form-check-label"
                    style="margin-left: 10px"
                    for="radio1"
                    >Daily</label
                  >
                </v-col>
                <v-col class="form-check">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="radio2"
                    name="optradio"
                    value="option2"
                    @click="Openmonthbookingcard(1)"
                  />
                  <label
                    class="form-check-label"
                    style="margin-left: 10px"
                    for="radio2"
                    >Weekly</label
                  >
                </v-col>
                <v-col class="form-check">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="radio3"
                    name="optradio"
                    value="option3"
                    @click="Openmonthbookingcard(2)"
                  />
                  <label class="form-check-label" style="margin-left: 10px"
                    >Monthly</label
                  >
                </v-col>
              </form>
            </v-row>
          </v-col>
          <!-- Second Col -->
          <v-col
            sm="8"
            lg="6"
            md="6"
            :class="fulldayChecked == true ? 'secondcolumn' : 'customcolumn'"
            style="background-color: #bbb"
          >
            <!-- Debug: Show which date picker sections are visible -->
            <div style="display: none;">
              Debug: openDaycard = {{ openDaycard }}, openWeekcard = {{ openWeekcard }}, openMonthcard = {{ openMonthcard }}
            </div>
            
            <!-- DAILY -->
            <v-row class="custom_daily" v-if="openDaycard == true">
              <v-col
                v-if="fulldayChecked == false && (hourlyChecked == true || freebookingChecked == true)"
                xs="12"
                sm="8"
                lg="4"
                md="4"
              >
                <label class="labelStyle">Start Time </label>
                <DatePicker
                  mode="time"
                  title="Start Time"
                  v-model="startTimeDaily"
                  :valid-hours="validateHours"
                  :is24hr="true"
                  :minute-increment="30"
                  @change="updateSearchDate()"
                />
              </v-col>
              <v-col v-if="fulldayChecked == true" xs="12" sm="8" lg="4" md="4">
              </v-col>
              <v-col
                v-if="fulldayChecked == false && (hourlyChecked == true || freebookingChecked == true)"
                sm="8"
                lg="4"
                md="4"
                class="every_custom"
              >
                <label class="labelStyle">End Time </label>
                <DatePicker
                  mode="time"
                  title="Start Time"
                  v-model="endTimeDaily"
                  :valid-hours="validateHours"
                  :is24hr="true"
                  :minute-increment="30"
                  @change="updateSearchDate()"
                />
              </v-col>
              <v-col v-if="fulldayChecked == true" xs="12" sm="8" lg="4" md="4">
              </v-col>
              <v-col sm="12" lg="6" md="6" class="first_daily_row">
                <label class="people_label"><b>Number of people</b></label>
                <v-text-field
                  v-model="maxPeopleDaily"
                  variant="outlined"
                  type="number"
                  min="1"
                ></v-text-field>
              </v-col>
            </v-row>
            <!-- <v-row v-if="openDaycard == true" style="margin-left: 70px;">
              </v-row> -->

            <!-- WEEK -->
            <v-row class="container">
              <div v-if="openWeekcard == true">
                <v-col>
                  <v-row class="weekly_gap">
                    <v-col
                      v-if="
                        fulldayChecked == false ||
                        hourlyChecked == true ||
                        freebookingChecked == true
                      "
                      sm="8"
                      lg="4"
                      md="4"
                    >
                      <label class="labelStyle">Start Time </label>
                      <DatePicker
                        class="time_padding"
                        mode="time"
                        title="Start Time"
                        v-model="startTimeWeekly"
                        :valid-hours="validateHours"
                        :is24hr="true"
                        :minute-increment="30"
                        @change="updateSearchDate()"
                      />
                    </v-col>
                    <v-col
                      v-if="
                        fulldayChecked == false ||
                        hourlyChecked == true ||
                        freebookingChecked == true
                      "
                      sm="8"
                      lg="4"
                      md="4"
                      class="every_custom"
                    >
                      <label class="labelStyle">End Time </label>
                      <DatePicker
                        class="time_padding"
                        mode="time"
                        title="Start Time"
                        v-model="endTimeWeekly"
                        :valid-hours="validateHours"
                        :is24hr="true"
                        :minute-increment="30"
                        @change="updateSearchDate()"
                      />
                    </v-col>
                    <v-col sm="4" lg="2" md="2" class="first_daily_row">
                      <label class="people_label"
                        ><b>Number of people</b></label
                      >
                      <v-text-field
                        v-model="maxPeopleWeekly"
                        class="peopleinputbox"
                        variant="outlined"
                        type="number"
                        min="1"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col class="custom_week">
                  <v-row>
                    <v-col sm="6" lg="3" md="3" class="form-check">
                      <label>Every week on:</label>
                    </v-col>
                    <v-col
                      sm="6"
                      lg="3"
                      md="3"
                      class="form-check custom_responsiveness"
                    >
                      <input
                        type="checkbox"
                        v-model="selectDays"
                        ass="form-check-input"
                        id="check1"
                        name="option1"
                        value="Sunday"
                      />
                      <label class="form-check-label" for="check1"
                        >Sunday</label
                      >
                    </v-col>
                    <v-col
                      sm="6"
                      lg="3"
                      md="3"
                      class="form-check custom_responsiveness"
                    >
                      <input
                        type="checkbox"
                        v-model="selectDays"
                        ass="form-check-input"
                        id="check2"
                        name="option2"
                        value="Monday"
                      />
                      <label class="form-check-label" for="check2"
                        >Monday</label
                      >
                    </v-col>
                    <v-col
                      sm="6"
                      lg="3"
                      md="3"
                      class="form-check custom_responsiveness"
                    >
                      <input
                        type="checkbox"
                        v-model="selectDays"
                        lass="form-check-input"
                        id="check3"
                        name="option3"
                        value="Tuesday"
                      />
                      <label class="form-check-label" for="check3"
                        >Tuesday</label
                      >
                    </v-col>
                  </v-row>
                </v-col>
                <v-col class="custom_week">
                  <v-row>
                    <v-col
                      sm="6"
                      lg="3"
                      md="3"
                      class="form-check custom_responsiveness"
                    >
                      <input
                        type="checkbox"
                        v-model="selectDays"
                        class="form-check-input"
                        id="check4"
                        name="option4"
                        value="Wednesday"
                      />
                      <label class="form-check-label" for="check4"
                        >Wednesday</label
                      >
                    </v-col>
                    <v-col
                      sm="6"
                      lg="3"
                      md="3"
                      class="form-check custom_responsiveness"
                    >
                      <input
                        type="checkbox"
                        v-model="selectDays"
                        class="form-check-input"
                        id="check5"
                        name="option4"
                        value="Thursday"
                      />
                      <label class="form-check-label" for="check5"
                        >Thursday</label
                      >
                    </v-col>
                    <v-col
                      sm="6"
                      lg="3"
                      md="3"
                      class="form-check custom_responsiveness"
                    >
                      <input
                        type="checkbox"
                        v-model="selectDays"
                        ass="form-check-input"
                        id="check6"
                        name="option5"
                        value="Friday"
                      />
                      <label class="form-check-label" for="check6"
                        >Friday</label
                      >
                    </v-col>
                    <v-col
                      sm="6"
                      lg="3"
                      md="3"
                      class="form-check custom_responsiveness"
                    >
                      <input
                        type="checkbox"
                        v-model="selectDays"
                        class="form-check-input"
                        id="check7"
                        name="option6"
                        value="Saturday"
                      />
                      <label class="form-check-label" for="check7"
                        >Saturday</label
                      >
                    </v-col>
                  </v-row>
                </v-col>
              </div>
            </v-row>

            <!-- MONTHLY -->
            <v-row class="container">
              <div v-if="openMonthcard == true">
                <v-col>
                  <v-row>
                    <v-col
                      v-if="fulldayChecked == false && (hourlyChecked == true || freebookingChecked == true)"
                      sm="8"
                      lg="4"
                      md="4"
                    >
                      <label class="labelStyle">Start Time </label>
                      <DatePicker
                        class="time_padding"
                        mode="time"
                        title="Start Time"
                        v-model="startTimeMonthly"
                        :valid-hours="validateHours"
                        :is24hr="true"
                        :minute-increment="30"
                        @change="updateSearchDate()"
                      />
                    </v-col>
                    <v-col
                      v-if="fulldayChecked == false && (hourlyChecked == true || freebookingChecked == true)"
                      sm="8"
                      lg="4"
                      md="4"
                      class="every_custom"
                    >
                      <label class="labelStyle">End Time </label>
                      <DatePicker
                        class="time_padding"
                        mode="time"
                        title="Start Time"
                        v-model="endTimeMonthly"
                        :valid-hours="validateHours"
                        :is24hr="true"
                        :minute-increment="30"
                        @change="updateSearchDate()"
                      />
                    </v-col>
                    <v-col sm="4" lg="2" md="2" class="first_daily_row">
                      <label class="people_label"
                        ><b>Number of people</b></label
                      >
                      <v-text-field
                        v-model="maxPeopleMonthly"
                        class="peopleinputbox"
                        variant="outlined"
                        type="number"
                        min="1"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col>
                  <v-row class="monthly_row">
                    <v-col
                      class="form-check custom_form"
                      style="padding-left: 1.5em"
                    >
                      <input
                        type="radio"
                        @click="checkedmonthlyday(0)"
                        class="form-check-input custom_input"
                        id="radio1"
                        name="optradio"
                        value="option1"
                        checked
                      />
                      <label for="radiobutton1">Day</label>
                    </v-col>
                    <v-col class="custom_text">
                      <input
                        v-model="MonthlyDays"
                        class="day_input"
                        variant="outlined"
                        type="number"
                        min="1"
                      />
                      <p>of every month</p>
                    </v-col>
                  </v-row>
                  <v-row class="monthly_row">
                    <v-col
                      class="form-check custom_form"
                      style="padding-left: 1.5em"
                    >
                      <input
                        type="radio"
                        @click="checkedmonthlyday(1)"
                        class="form-check-input"
                        id="radio2"
                        name="optradio"
                        value="option2"
                      />
                      <label for="radiobutton2">The</label>
                    </v-col>
                    <div class="custom_dropdowm">
                      <v-row>
                        <v-col cols="6">
                          <multiselect
                            class="first_dropdown"
                            v-model="CountDays"
                            deselect-label="Selected"
                            select-label="Click to select"
                            placeholder="Select Venue"
                            :options="Counts"
                            :searchable="false"
                            :allow-empty="false"
                            group-label="venueType"
                          >
                          </multiselect>
                        </v-col>
                        <v-col cols="6">
                          <multiselect
                            class="sec_dropdown"
                            v-model="SelectedWeekDay"
                            deselect-label="Selected"
                            select-label="Click to select"
                            placeholder="Select Venue"
                            :options="Weekdays"
                            :searchable="false"
                            :allow-empty="false"
                            group-label="venueType"
                          >
                          </multiselect>
                        </v-col>
                      </v-row>
                      of every month
                    </div>
                  </v-row>
                </v-col>
              </div>
            </v-row>
          </v-col>
          <v-col
            sm="8"
            lg="4"
            md="4"
            class="thirdcolumn"
            style="background-color: #ccc"
          >
            <!-- DAILY -->
            <v-row style="margin-left: 0.8px" v-if="openDaycard == true">
              <v-col>
                <label for="birthdaytime">Start Date:</label>
                <input
                  v-model="startDateDaily"
                  class="vc-time-picker vc-container vc-blue"
                  :min="minDate"
                  type="date"
                  id="birthdaytime"
                  name="birthdaytime"
                  @input="updateSelectedDate"
                  @change="updateSelectedDate"
                />
              </v-col>
              <v-col>
                <label for="birthdaytime">End Date:</label>
                <input
                  v-model="endDateDaily"
                  class="vc-time-picker vc-container vc-blue"
                  :min="minDate"
                  type="date"
                  id="birthdaytime"
                  name="birthdaytime"
                />
              </v-col>
            </v-row>

            <!-- WEEK -->
            <v-row style="display: inline" v-if="openWeekcard == true">
              <v-col>
                <label for="birthdaytime">Start Date:</label>
                <input
                  v-model="startDateWeekly"
                  class="vc-time-picker vc-container vc-blue"
                  :min="minDate"
                  type="date"
                  id="birthdaytime"
                  name="birthdaytime"
                  @input="updateSelectedDate"
                  @change="updateSelectedDate"
                />
              </v-col>
              <v-col>
                <label for="birthdaytime">End Date:</label>
                <input
                  v-model="endDateWeekly"
                  class="vc-time-picker vc-container vc-blue"
                  :min="minDate"
                  type="date"
                  id="birthdaytime"
                  name="birthdaytime"
                />
              </v-col>
            </v-row>

            <!-- Monthly -->
            <v-row v-if="openMonthcard == true" style="margin-left: 0.7px">
              <v-col>
                <label for="birthdaytime">Start Date:</label>
                <input
                  v-model="startDateMonthly"
                  class="vc-time-picker vc-container vc-blue"
                  :min="minDate"
                  type="date"
                  id="birthdaytime"
                  name="birthdaytime"
                  @input="updateSelectedDate"
                  @change="updateSelectedDate"
                />
              </v-col>
              <v-col>
                <label for="birthdaytime">End Date:</label>
                <input
                  v-model="endDateMonthly"
                  class="vc-time-picker vc-container vc-blue"
                  :min="minDate"
                  type="date"
                  id="birthdaytime"
                  name="birthdaytime"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <hr class="horLine" />

        <v-col cols="12" v-if="validationError">
          <div class="timeValidityText">
            Some date are already booked please verify and update your date and
            time.
          </div>
        </v-col>
        <v-col cols="12" v-if="showunavailabledates">
          <div class="timeValidityText">
            {{ unavailableDate }} these dates are not available for booking on
            given slots.
          </div>
        </v-col>
        <v-row>
          <v-col
            class=""
            style="margin-bottom: 10px; margin-top: 10px; margin-left: 20px"
          >
            <v-btn
              :style="{
                'background-color': this.buttonColor,
                color: 'var(--booking-button-font-color)',
              }"
              :loading="loading[3]"
              @click="AddMultBooking()"
            >
              + Add to Booking</v-btn
            >
          </v-col>
          <v-col class="col-lg-8" style="margin-bottom: 10px; margin-top: 10px">
            <v-btn
              style="background-color: #58595b; color: white"
              @click="Cancel()"
            >
              Cancel</v-btn
            >
          </v-col>
          <v-col class="col-lg-8" style="margin-bottom: 10px; margin-top: 10px">
          </v-col>
          <v-col class="col-lg-8" style="margin-bottom: 10px; margin-top: 10px">
          </v-col>
        </v-row>
      </v-card>
      </div>

    <section class="booking-wizard__calendar-section booking-wizard__calendar-section--below-wizard">
      <div class="booking-wizard__calendar-card">
        <header class="booking-wizard__calendar-head">
          <div class="booking-wizard__calendar-head-main">
            <div class="booking-wizard__calendar-icon" aria-hidden="true">
              <v-icon icon="mdi-calendar-month" size="22" />
            </div>
            <div>
              <h2 class="booking-wizard__calendar-title">Venue Availability Overview</h2>
              <p class="booking-wizard__calendar-subtitle">
                Browse booked times for {{ selectedVenueName || "this venue" }}. Shaded blocks are unavailable.
              </p>
            </div>
          </div>
          <div class="booking-wizard__calendar-legend" aria-hidden="true">
            <span class="booking-wizard__calendar-legend-item booking-wizard__calendar-legend-item--busy">Booked</span>
            <span class="booking-wizard__calendar-legend-item booking-wizard__calendar-legend-item--free">Available</span>
          </div>
        </header>

        <div class="booking-wizard__calendar-toolbar">
          <v-btn
            class="booking-wizard__calendar-nav-btn"
            icon="mdi-chevron-left"
            variant="outlined"
            size="small"
            @click="navigateCalendar('prev')"
          />
          <v-btn-toggle
            v-model="currentView"
            mandatory
            class="booking-wizard__calendar-view-toggle"
            density="compact"
            variant="outlined"
            @update:model-value="onViewChange"
          >
            <v-btn value="month" size="small">
              <v-icon start size="18">mdi-calendar-month</v-icon>
              Month
            </v-btn>
            <v-btn value="week" size="small">
              <v-icon start size="18">mdi-calendar-week</v-icon>
              Week
            </v-btn>
            <v-btn value="day" size="small">
              <v-icon start size="18">mdi-calendar-today</v-icon>
              Day
            </v-btn>
          </v-btn-toggle>
          <v-btn
            class="booking-wizard__calendar-nav-btn"
            icon="mdi-chevron-right"
            variant="outlined"
            size="small"
            @click="navigateCalendar('next')"
          />
        </div>

        <div class="booking-wizard__calendar-frame">
          <v-overlay :model-value="overlay" contained class="align-center justify-center">
            <BookingWizardLoader
              size="md"
              :color="buttonColor || circleColor"
            />
          </v-overlay>
          <full-calendar
            ref="fullCalendar"
            :options="calendarOptions"
            class="calendar booking-wizard__calendar"
          />
        </div>
      </div>
      <div id="main"></div>
    </section>
  </main>

  <footer-component />

  <v-dialog v-model="sessionTimerExpired" persistent max-width="480">
    <v-card>
      <v-card-title class="text-h6">Session expired</v-card-title>
      <v-card-text>
        Your booking session has expired. Please start again to secure your slot.
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          :style="{ backgroundColor: buttonColor, color: 'var(--booking-button-font-color)' }"
          variant="flat"
          @click="handleSessionExpiredConfirm"
        >
          OK
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import SearchByDateComponent from "@/components/SearchByDate/index.vue";
import BookingContactStep from "@/components/booking/BookingContactStep/index.vue";
import BookingSecondDetailStep from "@/components/booking/BookingSecondDetailStep/index.vue";
import BookingOpenFormsStep from "@/components/booking/BookingOpenFormsStep/index.vue";

import HeaderComponent from "@/components/common/HeaderComponent/index.vue";
import { mapActions } from "vuex";
import {
  setBookingSearchSelect,
  setMultiBookingSearchSelect,
  setBundleSearchSelect,
  setBookingDetail,
  setBundleBookingDetail,
  multiBookingsearch,
  getPricingDetail,
  getPriceBookingPage,
  generateAuthKeys,
  attachTransactionDetail,
  hppConfiguration,
  GetPaymentSettings,
  addAddonFacility,
  checkOverlapping,
  SaveImage,
  checkExistingOnetimeUser,
} from "../../../../server/config";
import {
  getBookingVenueSettings,
  getbookedVenues,
  getBookingGeneral,
  getBookingSearchSettings,
  getTemporarybookingDetails,
  getMultibookingDetails,
  getBundleBookingVenueSettings,
  getVenueOptions,
  getBookingVenueTypeSettings,
  getBookingDetailSettings,
} from "../../../store/vuexStore";
import * as ENV from "@/config.js";
import Multiselect from "vue-multiselect";
import moment from "moment";
import { search } from "../../../../server/api";
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  formatVenueDescription,
  hasVenueDescriptionContent,
} from "@/utils/venueDescription";
import { applyBookingTheme, getContrastTextColor, mixWithWhite } from "@/utils/bookingTheme.js";
import {
  BOOKING_TIME_ZONE,
  formatBookingTimeRange as formatBookingTimeRangeLabel,
  formatLocalDateKey,
  getBookingWeekdayName,
  parseBookingDateTimeRange,
  toCalendarEventInput,
} from "@/utils/bookingDateTime.js";
import { scrollElementIntoView } from "@/utils/scrollIntoView.js";
import BookingWizardLoader from "@/components/booking/BookingWizardLoader/index.vue";
import {
  getOtpBookingHoursRemaining,
  getWizardSessionDurationHours,
  markOtpBookingCheckout,
  setOtpBookingHoursRemaining,
  syncLegacyOtpSessionFromStorage,
} from "@/utils/otpSession.js";

export default {
  components: {
    SearchByDateComponent,
    BookingContactStep,
    BookingSecondDetailStep,
    BookingOpenFormsStep,
    HeaderComponent,
    BookingWizardLoader,
    Multiselect,
    FullCalendar,
  },
  props: {
    booking: Object,
    maxPeople: Number,
  },
  data() {
    return {
      minDate: "",
      startTimeMonthly: new Date(
        new Date("2012/10/09 00:00").setHours(new Date().getHours())
      ),
      endTimeMonthly: new Date(
        new Date(new Date("2012/10/09 00:00")).setHours(
          new Date().getHours() + 1
        )
      ),
      startTimeDaily: new Date(
        new Date("2012/10/09 00:00").setHours(new Date().getHours())
      ),
      endTimeDaily: new Date(
        new Date(new Date("2012/10/09 00:00")).setHours(
          new Date().getHours() + 1
        )
      ),
      startDateDaily: "",
      endDateDaily: "",
      startDateWeekly: "",
      endDateWeekly: "",
      startDateMonthly: "",
      endDateMonthly: "",
      startTimeWeekly: new Date(
        new Date("2012/10/09 00:00").setHours(new Date().getHours())
      ),
      endTimeWeekly: new Date(
        new Date(new Date("2012/10/09 00:00")).setHours(
          new Date().getHours() + 1
        )
      ),
      MonthlyDays: "",
      validationError: false,
      selectedWeekDay: [],
      venueIds: [],
      maxPeopleDaily: "",
      maxPeopleWeekly: "",
      maxPeopleMonthly: "",
      date: new Date(),
      dateObj: new Date(),
      selectdatetime: false,
      opensinglebookingcart: false,
      openmultibookingbox: false,
      overlay: false,
      searchOption: [],
      tab: null,
      circleColor: "",
      buttonColor: "",
      WorkingHours: [],
      events: [],
      venuePhoto: "",
      mapPhoto: "",
      text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
      selectedFilter: "",
      bookingOptions: [],
      venueTypeOptions: [],
      selectVenueType: null,
      venueType: [],
      selectedvenueType: "",
      fulldayChecked: false,
      hourlyChecked: false,
      freebookingChecked: false,
      selectedVenueName: "",
      currentUniqueVenueId: "", // Store FCC-VEN-XX ID for API calls
      storeaddedbookingdetails: [],
      storemultiBookingCartData: [],
      multiselectedVenues: [],
      openmultibookingcart: false,
      Counts: ["First", "Second", "Third", "Fourth", "Last"],
      CountDays: "",
      Weekdays: [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ],
      SelectedWeekDay: "",
      filteredDatesByOccurence: [],
      checkedDay: true,
      checkedThe: false,
      openDaycard: true,
      openWeekcard: false,
      openMonthcard: false,
      percentagebar: 0,
      showzoomedImage: false,
      showmapzoomedImage: false,
      selectDays: [],
      showunavailabledates: false,
      unavailableDate: [],
      venueId: "",
      loading: [],
      validHours: [],
      singleBookingActive: false,
      multiBookingActive: false,
      multiBookingFlowActive: false,
      showMultiBookingButton: true,
      priceOfHalfHour: "",
      // venuePriceDetails: [],
      profithourlyprice: 0,
      profitfulldayyprice: 0,
      bookingVenuesId: [],
      facility: ENV.VUE_APP_FACILITY,
      setupTime: "",
      setdownTime: "",
      checksetupTime: false,
      checksetdownTime: false,
      isBooked: false,
      otpUserBookingHours: null,
      readOnlyOTP: false,
      otpUserDataFetched: false, // Flag to prevent duplicate OTP API calls
      description: "",
      venuesFromNewAPI: [], // Store venue data from new API endpoint
      currentView:
        typeof window !== "undefined" && window.innerWidth <= 768 ? "day" : "week",
      calendarRefreshInterval: null, // Store interval ID for auto-refresh
      wizardStep: 1,
      wizardContactDetails: {},
      confirmInProgress: false,
      contactStepValid: false,
      wizardSlotReserved: false,
      wizardDetailsSubmitted: false,
      wizardCalculatedAmount: 0,
      wizardPaymentRate: 0,
      wizardStoreVenueBlockId: null,
      wizardPricingLoading: false,
      wizardPaymentError: "",
      venueDropdownOpen: false,
      wizardLoading: false,
      wizardLoadingMessage: "Preparing your booking…",
      sessionTimerExpired: false,
      sessionTimerInterval: null,
      disableORenable2ndDetailPage: false,
      isEmbededOpenFormEnable: false,
      embededUrl: "",
      detailsSettings: [],
      facilityAddonDetails: [
        { title: "Overlap", data: [] },
        { title: "Non-Overlap", data: [] },
      ],
      facilityAddonTemp: [],
      enableOverlap: false,
      enableNonOverlap: false,
      wizardSecondPageDetails: [],
      wizardDetailQuestions: [],
      openFormsProcessing: false,
      showScrollDownHint: false,
    };
  },
  computed: {
    hideTimeForShowgroundFullDay() {
      const f = (this.facility || "").toLowerCase();
      const isShowground = f === "showground" || f === "showgrounds";
      return isShowground && this.fulldayChecked === true;
    },
    hasDescriptionContent() {
      return hasVenueDescriptionContent(this.description);
    },
    hasVenuePhoto() {
      return Boolean(this.resolveMediaUrl(this.venuePhoto));
    },
    hasMapPhoto() {
      return Boolean(this.resolveMediaUrl(this.mapPhoto));
    },
    hasVenueMedia() {
      return this.hasVenuePhoto || this.hasMapPhoto;
    },
    hasDualVenueMedia() {
      return this.hasVenuePhoto && this.hasMapPhoto;
    },
    renderedDescription() {
      return formatVenueDescription(this.description);
    },
    showEmptyBookingCart() {
      return (
        this.opensinglebookingcart === false &&
        this.openmultibookingcart === false &&
        this.singleBookingActive === true
      );
    },
    showSingleBookingCart() {
      return (
        this.opensinglebookingcart === true || this.singleBookingActive === false
      );
    },
    singleCartBookingsForVenue() {
      return (this.storeaddedbookingdetails || []).filter(
        (booking) => booking.venueName === this.selectedVenueName
      );
    },
    multiCartBookingsForVenue() {
      return (this.storemultiBookingCartData || []).filter(
        (booking) => booking.venueName === this.selectedVenueName
      );
    },
    hasBookingInCart() {
      const singleCount = (this.storeaddedbookingdetails || []).length;
      const multiCount = (this.storemultiBookingCartData || []).length;
      return singleCount > 0 || multiCount > 0;
    },
    wizardSessionBooking() {
      const single = this.singleCartBookingsForVenue[0];
      if (single) return single;
      return this.multiCartBookingsForVenue[0] || null;
    },
    selectedVenueMeta() {
      if (!this.selectedVenueName || !this.venuesFromNewAPI) {
        return null;
      }
      return this.venuesFromNewAPI.find(
        (venue) =>
          (venue.venuename || venue.venueName) === this.selectedVenueName
      );
    },
    selectedVenueCapacity() {
      const venue = this.selectedVenueMeta;
      if (!venue) return null;
      return venue.maxpeople || venue.maxPeople || null;
    },
    canProceedWizard() {
      if (this.wizardStep === 1) {
        return Boolean(this.selectedVenueName);
      }
      if (this.wizardStep === 2) {
        return this.hasBookingInCart;
      }
      if (this.wizardStep === 3) {
        return this.hasBookingInCart && this.contactStepValid;
      }
      if (this.wizardStep === this.wizardExtraStep && this.showSecondDetailStep) {
        return this.hasBookingInCart;
      }
      return false;
    },
    showSecondDetailStep() {
      return (
        this.disableORenable2ndDetailPage === true &&
        !this.isEmbededOpenFormEnable
      );
    },
    showOpenFormsStep() {
      return this.isEmbededOpenFormEnable && Boolean(this.embededUrl);
    },
    showWizardExtraStep() {
      return this.showSecondDetailStep || this.showOpenFormsStep;
    },
    wizardExtraStep() {
      return 4;
    },
    wizardReviewStep() {
      return this.showWizardExtraStep ? 5 : 4;
    },
    wizardContactFullName() {
      const first = this.wizardContactDetails?.FirstName || "";
      const last = this.wizardContactDetails?.LastName || "";
      return `${first} ${last}`.trim() || "—";
    },
    wizardBookingTypeLabel() {
      if (this.freebookingChecked) return "Free booking";
      if (this.fulldayChecked) return "Full day";
      if (this.hourlyChecked) return "Hourly";
      return "Standard";
    },
    backButtonStyle() {
      const accent = this.buttonColor || "#0275FF";
      return {
        backgroundColor: mixWithWhite(accent, 0.68),
        color: accent,
        border: `1px solid ${mixWithWhite(accent, 0.5)}`,
      };
    },
    scrollHintAccentStyle() {
      const accent = this.buttonColor || "#0275FF";
      const brand = this.circleColor || accent;
      const onAccent = getContrastTextColor(accent);
      return {
        "--booking-button-color": accent,
        "--booking-brand-color": brand,
        "--scroll-hint-on-accent": onAccent,
      };
    },
    showWizardSlotPicker() {
      return (
        this.wizardStep === 2 &&
        this.selectdatetime &&
        !this.hasBookingInCart
      );
    },
    calendarOptions() {
      // Map view names to FullCalendar view names
      const viewMapping = this.getViewMapping();
      
      return {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        initialView: viewMapping[this.currentView] || 'timeGridWeek',
        headerToolbar: {
          left: '',
          center: 'title',
          right: ''
        },
        height: 'auto',
        contentHeight: 'auto',
        timeZone: BOOKING_TIME_ZONE,
        displayEventEnd: true,
        slotMinHeight: window.innerWidth <= 768 ? 36 : 32,
        events: this.events,
        slotMinTime: '06:00:00',
        slotMaxTime: '23:00:00',
        // Ensure all time slots are rendered
        expandRows: false,
        // Force rendering of all hours
        validRange: null,
        firstDay: 1, // Monday
        allDaySlot: false,
        slotDuration: '01:00:00',
        slotLabelInterval: '01:00:00',
        nowIndicator: true,
        hour12: false,
        slotLabelFormat: {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        },
        dayHeaderFormat: { weekday: 'short', day: 'numeric' }, // Use 'short' instead of 'long' to prevent overlapping
        // Enable scrolling on mobile
        scrollTime: '06:00:00',
        scrollTimeReset: false,
        eventClassNames: 'booking-cal-event',
      };
    },
  },

  async created() {
    // Show loading overlay during data fetch
    this.overlay = true;
    
    try {
      // Call bookingGetConfig if store is empty (direct navigation or refresh)
      const existingConfig = getBookingGeneral();
      if (!existingConfig || Object.keys(existingConfig).length === 0) {
        await this.bookingGetConfig();
      }

      this.bookingVenuesId = getBookingVenueSettings();
      this.storeaddedbookingdetails = getTemporarybookingDetails();
      if (this.storeaddedbookingdetails === undefined) {
        sessionStorage.removeItem(this.selectedVenueName);
        this.storeaddedbookingdetails = [];
      }

      this.storemultiBookingCartData = getMultibookingDetails();
      if (this.storemultiBookingCartData === undefined) {
        this.storemultiBookingCartData = [];
      }
      if (Array.isArray(this.storemultiBookingCartData) && this.storemultiBookingCartData.length > 0) {
        this.openmultibookingcart = true;
        this.multiBookingFlowActive = true;
      }

      const venueType = decodeURIComponent(
        location.search.split("venueType=")[1].replace(/_/g, " ")
      );
      this.selectedvenueType = venueType;
      sessionStorage.setItem("selectedvenuetype", venueType);
      await this.loadOTPUserData();
      await this.loadWizardDetailSettings();

      // Fetching the venue names to show on the calendar
      const bookingGeneral = getBookingGeneral() || {};
      this.circleColor = bookingGeneral.color || "#ffffff";
      this.buttonColor = bookingGeneral.buttoncolor || "#0275FF";
      applyBookingTheme(bookingGeneral);
      
      var cols = document.getElementsByClassName("vuecal__menu");
      for (let i = 0; i < cols.length; i++) {
        cols[i].style.backgroundColor = this.buttonColor;
        cols[i].style.color = getContrastTextColor(this.buttonColor);
        // cols[i].style.backgroundColor = this.circleColor;
      }

      const bookingSearchSettings = getBookingSearchSettings() || {};
      let bookingHours = bookingSearchSettings.workinghours || [];
      
      for (let index = 0; index < bookingHours.length; index++) {
        let element = bookingHours[index];
        if (element.venuetype === this.selectedvenueType) {
          this.WorkingHours = element.workingHoursConfig;
          break;
        }
      }

      let bookingSearchSetting = bookingSearchSettings.nonprofitableorganisation || [];
      for (let index = 0; index < bookingSearchSetting.length; index++) {
        let element = bookingSearchSetting[index];
        if (element.venuetype === this.selectedvenueType) {
          sessionStorage.setItem(
            "enablekey",
            element?.enableprofitableorganisation
          );
          sessionStorage.setItem("enablebusinesskey", element?.enablebusinesskey);
        }
      }

      // Fetching venues setting from vuex store
      this.adminVenues = getBookingVenueSettings() || [];
      this.bundleVenues = getBundleBookingVenueSettings() || [];
      
      // Set default booking options
      this.fulldayChecked = false;
      this.hourlyChecked = true;
      this.freebookingChecked = false;
      this.checksetupTime = true;
      this.checksetdownTime = true;
      this.singleBookingActive = true;
      this.multiBookingActive = false; // Default to false, will be set to true from API if needed

      // Fetch venue options from new API endpoint
      await this.getVenueOptionsAction(this.selectedvenueType);
      
      // Get venue options from Vuex store and store in component variable
      const venueOptionsData = getVenueOptions();
      this.venuesFromNewAPI = venueOptionsData || [];
      
      // Check if multiple bookings should be enabled based on venue type settings
      const venueTypeSettings = getBookingVenueTypeSettings() || [];
      const currentVenueTypeSetting = venueTypeSettings.find(setting => 
        setting.venueType === this.selectedvenueType
      );
      
      if (
        currentVenueTypeSetting &&
          currentVenueTypeSetting.bookingTypes && 
        currentVenueTypeSetting.bookingTypes.enableMultiBookings === true
      ) {
        this.multiBookingActive = true;
        this.showMultiBookingButton = true;
      }
      this.updateSingleBookingButtonState();
      
      // Set booking flags from API data
      if (currentVenueTypeSetting) {
        this.fulldayChecked = currentVenueTypeSetting.fullday === true;
        this.hourlyChecked = currentVenueTypeSetting.enablehourly === true;
        this.freebookingChecked = currentVenueTypeSetting.enablefreebooking === true;
      }
      
      if (this.venuesFromNewAPI && Array.isArray(this.venuesFromNewAPI) && this.venuesFromNewAPI.length > 0) {
        // Use API response - filter venues for current facility and enabled venues
        const currentFacility = ENV.VUE_APP_FACILITY;
        
        this.venuesFromNewAPI.forEach((venue) => {
          // Filter conditions:
          // 1. Must match current facility OR have no facility set (for backward compatibility)
          // 2. Must be enabled (enablevenuename === true)
          // 3. Must have a valid venue name
          // NOTE: API returns lowercase field names (venuename, not venueName)
          const matchesFacility = !venue.facility || venue.facility === '' || venue.facility === currentFacility;
          const isEnabled = venue.enablevenuename === true;
          const hasValidName = venue.venuename && venue.venuename.trim() !== '';
          
          if (matchesFacility && isEnabled && hasValidName) {
            // Check if this venue name is already added (avoid duplicates)
            if (!this.venueTypeOptions.includes(venue.venuename)) {
              this.venueTypeOptions.push(venue.venuename);
              
              // Determine if it's a bundle venue or regular venue
              const isBundleVenue = venue.bundlevenueids && Array.isArray(venue.bundlevenueids) && venue.bundlevenueids.length > 0;
              
              this.venueIds.push({
                venueid: isBundleVenue ? venue.bundlevenueids : venue.venueid,
                venueName: venue.venuename,
                venuePhoto: venue.venuephoto || venue.venuePhoto || "",
                mapPhoto: venue.mapphoto || venue.mapPhoto || "",
                bundleVenueId: isBundleVenue ? venue.bundlevenueids : "",
                uniqueVenueId: venue.id, // Store the FCC-VEN-XX ID
              });
            }
          }
        });
      }
      
      // Check if no venues found from API
      if (this.venueTypeOptions.length === 0) {
        // Set fallback message
        this.venueTypeOptions = ["No Venues Available for Chosen venue group"];
        this.venueIds = [];
      }

      this.selectedVenueName = this.venueTypeOptions[0];
      if (this.storeaddedbookingdetails.length > 0) {
        this.opensinglebookingcart = true;
      }

      if (typeof window !== "undefined" && window.innerWidth <= 768) {
        this.currentView = "day";
      }

      if (sessionStorage.getItem("wizardPaymentStarted") === "1") {
        sessionStorage.removeItem("wizardPaymentStarted");
        this.resetWizardBookingState({
          keepVenue: true,
          keepReservation: true,
        });
      }
      
    } catch (error) {
      // Error during initialization
    } finally {
      // Always set overlay to false - all async data loading is complete
      this.overlay = false;
    }
  },
  watch: {
    wizardStep() {
      this.$nextTick(() => this.updateScrollDownHint());
    },
    async selectedVenueName(selectedValue) {
      sessionStorage.setItem("selectedvenuename", selectedValue);

      this.description = "";
      this.venuePhoto = "";
      this.mapPhoto = "";
      
      // First, get the uniqueVenueId from venueIds array
      let uniqueVenueIdFromSelected = null;
      for (let index = 0; index < this.venueIds.length; index++) {
        const element = this.venueIds[index];
        if (selectedValue === element.venueName) {
          uniqueVenueIdFromSelected = element.uniqueVenueId;
          break;
        }
      }
      
      let selectedVenueData = null;
      
      // Get venue data from component variable (from new API endpoint)
      // NOTE: API returns lowercase field names (venuename, not venueName)
      if (this.venuesFromNewAPI && Array.isArray(this.venuesFromNewAPI)) {
        // First try to find by uniqueVenueId from venueIds array (and ensure it's enabled)
        if (uniqueVenueIdFromSelected) {
          selectedVenueData = this.venuesFromNewAPI.find(venue => 
            venue.id === uniqueVenueIdFromSelected && venue.enablevenuename === true
          );
        }
        // If not found by ID, fall back to name-based search (only enabled venues)
        if (!selectedVenueData) {
          // CRITICAL: Only find enabled venues with this name, and prefer the one matching uniqueVenueIdFromSelected
          const enabledVenuesWithName = this.venuesFromNewAPI.filter(venue => 
            (venue.venuename === this.selectedVenueName || venue.venueName === this.selectedVenueName) &&
            venue.enablevenuename === true
          );
          
          if (enabledVenuesWithName.length > 0) {
            // If we have uniqueVenueIdFromSelected and it matches one of these enabled venues, use it
            if (uniqueVenueIdFromSelected) {
              const matchedById = enabledVenuesWithName.find(v => v.id === uniqueVenueIdFromSelected);
              if (matchedById) {
                selectedVenueData = matchedById;
              } else {
                // Use first enabled venue with this name
                selectedVenueData = enabledVenuesWithName[0];
              }
            } else {
              // Use first enabled venue with this name
              selectedVenueData = enabledVenuesWithName[0];
            }
          }
        }
      }
      
      if (selectedVenueData) {
        /* VENUE DATA STRUCTURE REFERENCE (From API: /ba/api/public/getvenue/{venueType})
         * 
         * selectedVenueData contains:
         * {
         *   id: string,                    // Venue identifier (e.g., "FCC-VEN-1")
         *   venueID: number,               // Numeric venue ID (e.g., 111)
         *   bundleVenueIds: array|null,    // Array of venue IDs if bundle, null otherwise
         *   facility: string,              // Facility name (e.g., "fchq", "tennis")
         *   venueName: string,             // Display name (e.g., "Testing Venue 4")
         *   maxPeople: number,             // Maximum capacity
         *   minPeople: number,             // Minimum capacity
         *   venueType: string,             // Venue group (e.g., "Fairfield Park Grass Court")
         *   venuepricing: string,          // Pricing configuration ID
         *   venuePhoto: string,            // Photo URL or ID
         *   mapPhoto: string,              // Map photo URL or ID
         *   crn2: string,                  // CRN code
         *   enablevenuename: boolean,      // Whether venue is enabled
         *   adminemail: string,            // Admin contact email
         *   setuptime: string,             // Setup time (e.g., "00:00")
         *   setdowntime: string,           // Setdown time (e.g., "00:60")
         *   description: string            // Venue description
         * }
         */
        
        // Set venue properties from API data
        this.setupTime = selectedVenueData.setdowntime || selectedVenueData.setuptime || "";
        this.setdownTime = selectedVenueData.setuptime || selectedVenueData.setdowntime || "";
        this.description = selectedVenueData.description || "";
        sessionStorage.setItem("crn2", selectedVenueData.crn2 || "");
        
        // CRITICAL: Save the correct venue ID - this is the same ID used in getcalendar
        // Use this stored ID everywhere else (book button, etc.)
        if (selectedVenueData.id) {
          // Ensure id is a single string (not array)
          let venueId = selectedVenueData.id;
          if (Array.isArray(venueId)) {
            venueId = venueId[0]; // Take first element if array
          }
          let idStr = String(venueId).trim();
          // If somehow it contains multiple IDs (comma-separated), take first
          if (idStr.includes(',')) {
            idStr = idStr.split(',')[0].trim();
          }
          this.currentUniqueVenueId = idStr;
        }
      } else {
        // Fallback to old venue arrays
        for (let j = 0; j < this.adminVenues.length; j++) {
          const venue = this.adminVenues[j];
          if (venue.venueName === this.selectedVenueName) {
            selectedVenueData = venue;
            this.setupTime = venue.setdowntime;
            this.setdownTime = venue.setuptime;
            this.description = venue.description || "";
            sessionStorage.setItem("crn2", venue.crn2);
            break;
          }
        }
        
        // Check bundle venues if not found in regular venues
        if (!selectedVenueData) {
          for (let j = 0; j < this.bundleVenues.length; j++) {
            const venue = this.bundleVenues[j];
            if (venue.venueName === this.selectedVenueName) {
              selectedVenueData = venue;
              this.setupTime = venue.setdowntime;
              this.setdownTime = venue.setuptime;
              this.description = venue.description || "";
              sessionStorage.setItem("crn2", venue.crn2);
              break;
            }
          }
        }
      }
      let matchedVenueIdEntry = null;
      for (let index = 0; index < this.venueIds.length; index++) {
        const element = this.venueIds[index];
        if (selectedValue === element.venueName) {
          matchedVenueIdEntry = element;
          this.venueId = element.venueid;
          break;
        }
      }

      this.applyVenueMedia(selectedVenueData, matchedVenueIdEntry);

      this.updateSingleBookingButtonState();
      this.selectdatetime = false;
      this.priceOfHalfHour = "25";
      this.events = [];

      await this.refreshCalendarData(selectedValue);
      this.setupCalendarAutoRefresh();
    },

    storeaddedbookingdetails: {
      handler() {
        this.updateSingleBookingButtonState();
      },
      deep: true,
    },
  },
  async mounted() {
    // OTP user data is already loaded in created() hook
    
    setTimeout(() => {
      var bottomBar = document.getElementsByClassName("vuecal__title-bar");
      let lightThemeColor = this.circleColor;
      lightThemeColor = lightThemeColor.replace(
        lightThemeColor.substring(7, 9),
        "4d"
      );
      for (let i = 0; i < bottomBar.length; i++) {
        bottomBar[i].style.backgroundColor = lightThemeColor;
      }
    }, 2000);
    this.setMinDate();
    
    if (this.selectedVenueName) {
      this.setupCalendarAutoRefresh();
    }

    if (sessionStorage.getItem("countDownTarget")) {
      this.startSessionTimer();
    }

    this._wizardScrollHintHandler = () => {
      if (this._wizardScrollHintRaf) return;
      this._wizardScrollHintRaf = requestAnimationFrame(() => {
        this._wizardScrollHintRaf = null;
        this.updateScrollDownHint();
      });
    };
    window.addEventListener("scroll", this._wizardScrollHintHandler, {
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", this._wizardScrollHintHandler, { passive: true });
    this.$nextTick(() => this.updateScrollDownHint());
  },

  beforeUnmount() {
    if (this._wizardScrollHintRaf) {
      cancelAnimationFrame(this._wizardScrollHintRaf);
      this._wizardScrollHintRaf = null;
    }
    if (this._wizardScrollHintHandler) {
      window.removeEventListener("scroll", this._wizardScrollHintHandler, true);
      window.removeEventListener("resize", this._wizardScrollHintHandler);
    }
    // Clear the interval when component is destroyed
    if (this.calendarRefreshInterval) {
      clearInterval(this.calendarRefreshInterval);
      this.calendarRefreshInterval = null;
    }
    this.clearSessionTimer();
    document.body.classList.remove("venue-dropdown-open");
  },


  methods: {
    async refreshCalendarData(selectedValue) {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = String(today.getMonth() + 1).padStart(2, '0');
      const monthYearFormatted = `${currentYear}-${currentMonth}`;

      let selectedVenueData = null;
      if (this.venuesFromNewAPI && Array.isArray(this.venuesFromNewAPI)) {
        selectedVenueData = this.venuesFromNewAPI.find(venue => 
          (venue.venuename || venue.venueName) === (selectedValue || this.selectedVenueName)
        );
      }
      if (selectedVenueData && selectedVenueData.id) {
        let priavaIds = [];
        
        if (selectedVenueData.bundlevenueids && Array.isArray(selectedVenueData.bundlevenueids) && selectedVenueData.bundlevenueids.length > 0) {
          priavaIds = selectedVenueData.bundlevenueids;
        } else if (selectedVenueData.venueid !== undefined) {
          priavaIds = [selectedVenueData.venueid];
        } else {
          return;
        }
        
        let venueIdForCalendar = selectedVenueData.id;
        if (Array.isArray(venueIdForCalendar)) {
          venueIdForCalendar = venueIdForCalendar[0];
        }
        venueIdForCalendar = String(venueIdForCalendar).trim();
        if (venueIdForCalendar.includes(',')) {
          venueIdForCalendar = venueIdForCalendar.split(',')[0].trim();
        }
        sessionStorage.setItem("selectedVenueUniqueId", venueIdForCalendar);
        this.currentUniqueVenueId = venueIdForCalendar;
        
        await this.getBookedVenues({
          facility: ENV.VUE_APP_FACILITY,
          monthyear: monthYearFormatted,
          uniquevenueId: venueIdForCalendar,
          priavaids: priavaIds
        });
        
        let bookedVenuesData = getbookedVenues() || [];
        if (JSON.stringify(bookedVenuesData) !== "[]") {
          this.appendEventData(bookedVenuesData, selectedValue || this.selectedVenueName);
        }
      }
    },
    
    setupCalendarAutoRefresh() {
      if (this.calendarRefreshInterval) {
        clearInterval(this.calendarRefreshInterval);
        this.calendarRefreshInterval = null;
      }
      
      this.calendarRefreshInterval = setInterval(async () => {
        if (this.selectedVenueName) {
          await this.refreshCalendarData(this.selectedVenueName);
        }
      }, 10000);
    },
    
    loadOTPUserData() {
      if (this.otpUserDataFetched) {
        return Promise.resolve();
      }

      syncLegacyOtpSessionFromStorage();
      const storedHours = getOtpBookingHoursRemaining();
      this.otpUserBookingHours = storedHours;
      this.readOnlyOTP = sessionStorage.getItem("readOnlyOTP") === "true";

      const onetimeToken = sessionStorage.getItem("onetimeToken");
      if (onetimeToken && storedHours === null) {
        return this.checkOTPUserData().finally(() => {
          this.otpUserDataFetched = true;
        });
      }

      this.otpUserDataFetched = true;
      return Promise.resolve();
    },
    
    // Helper method to get view mapping
    getViewMapping() {
      return {
        'month': 'dayGridMonth',
        'week': 'timeGridWeek',
        'day': 'timeGridDay'
      };
    },
    
    onViewChange() {
      // View change is handled by v-model binding to currentView
      // Use nextTick to ensure the calendar instance is ready
      this.$nextTick(() => {
        if (this.$refs.fullCalendar?.getApi) {
          const calendarApi = this.$refs.fullCalendar.getApi();
          if (calendarApi) {
            const targetView = this.getViewMapping()[this.currentView];
            if (targetView) {
              calendarApi.changeView(targetView);
            }
          }
        }
      });
    },
    
    navigateCalendar(direction) {
      this.$nextTick(() => {
        if (this.$refs.fullCalendar?.getApi) {
          const calendarApi = this.$refs.fullCalendar.getApi();
          if (calendarApi) {
            if (direction === 'prev') {
              calendarApi.prev();
            } else if (direction === 'next') {
              calendarApi.next();
            }
          }
        }
      });
    },
    
    async checkOTPUserData() {
      const onetimeToken = sessionStorage.getItem("onetimeToken");
      const email = sessionStorage.getItem("email");
      const venueType =
        sessionStorage.getItem("selectedvenuetype") ||
        this.selectedvenueType ||
        "";

      if (!onetimeToken || !email || !venueType) {
        return;
      }

      try {
        const data = await checkExistingOnetimeUser(email, venueType);
        if (data?.status === 1 && data?.response) {
          if (
            data.response.bookinghours !== undefined &&
            data.response.bookinghours !== null
          ) {
            this.otpUserBookingHours = Number(data.response.bookinghours);
            setOtpBookingHoursRemaining(this.otpUserBookingHours);
          }
          if (data.response.id) {
            sessionStorage.setItem("otpUserId", data.response.id);
          }
          this.readOnlyOTP = true;
          sessionStorage.setItem("readOnlyOTP", "true");
        }
      } catch {
        // optional refresh
      }
    },
    
    ...mapActions({
      bookingGetConfig: `${ENV.VUE_APP_FACILITY}_booking/bookingGetConfig`,
      getBookedVenues: `${ENV.VUE_APP_FACILITY}_booking/getBookedVenues`,
      getVenueOptionsAction: `${ENV.VUE_APP_FACILITY}_booking/getVenueOptions`,
      adminvenueTypeSettings: `${ENV.VUE_APP_FACILITY}_admin/adminvenueTypeSettings`,
      setTempBooking: `${ENV.VUE_APP_FACILITY}_booking/setTempBooking`,
      setMultiBookingData: `${ENV.VUE_APP_FACILITY}_booking/setMultiBookingData`,
    }),
    setMinDate() {
      const dtToday = new Date();
      const month = dtToday.getMonth() + 1;
      let day = dtToday.getDate();
      const year = dtToday.getFullYear();

      const formattedMonth = month < 10 ? "0" + month : month;
      const formattedDay = day < 10 ? "0" + day : day;

      this.minDate = `${year}-${formattedMonth}-${formattedDay}`;
    },
    resolveMediaUrl(value) {
      if (value == null) {
        return "";
      }

      const text = String(value).trim();
      if (
        !text ||
        text === "null" ||
        text === "undefined" ||
        text === "0"
      ) {
        return "";
      }

      return text;
    },

    getMediaFromSource(source) {
      if (!source || typeof source !== "object") {
        return { venuePhoto: "", mapPhoto: "" };
      }

      return {
        venuePhoto: this.resolveMediaUrl(
          source.venuePhoto ?? source.venuephoto
        ),
        mapPhoto: this.resolveMediaUrl(source.mapPhoto ?? source.mapphoto),
      };
    },

    applyVenueMedia(...sources) {
      let venuePhoto = "";
      let mapPhoto = "";

      for (const source of sources) {
        const media = this.getMediaFromSource(source);
        if (!venuePhoto && media.venuePhoto) {
          venuePhoto = media.venuePhoto;
        }
        if (!mapPhoto && media.mapPhoto) {
          mapPhoto = media.mapPhoto;
        }
      }

      this.venuePhoto = venuePhoto;
      this.mapPhoto = mapPhoto;

      if (venuePhoto) {
        sessionStorage.setItem("venuePhoto", venuePhoto);
      } else {
        sessionStorage.removeItem("venuePhoto");
      }

      if (mapPhoto) {
        sessionStorage.setItem("mapPhoto", mapPhoto);
      } else {
        sessionStorage.removeItem("mapPhoto");
      }
    },

    openvenueImage() {
      this.showzoomedImage = true;
    },
    onVenueDropdownOpen() {
      this.venueDropdownOpen = true;
      document.body.classList.add("venue-dropdown-open");
    },
    onVenueDropdownClose() {
      this.venueDropdownOpen = false;
      document.body.classList.remove("venue-dropdown-open");
    },
    openmapImage() {
      this.showmapzoomedImage = true;
    },
    async book() {
      // OLD FLOW DISABLED — checkout uses the booking wizard (reserve on step 2, confirmAndPay on step 4).
      return;

      /* eslint-disable no-unreachable */
      // Multi-booking takes highest priority - check this first
      const isMultiBooking = this.openmultibookingcart === true && 
                            this.storemultiBookingCartData && 
                            this.storemultiBookingCartData.length > 0;
      
      // Check if bundleVenueIds is an array with items (bundle venue) or null/empty (single venue)
      const isBundleVenue = Array.isArray(this.storeaddedbookingdetails?.[0]?.bundleVenueIds) && 
                           this.storeaddedbookingdetails[0].bundleVenueIds.length > 0;
      
      // Set BookingType with multi-booking taking priority
      let bookingType = "singlebooking";
      if (isMultiBooking) {
        bookingType = "multibooking";
      } else if (isBundleVenue) {
        bookingType = "bundlebooking";
      }
      
      sessionStorage.setItem("BookingType", bookingType);
      if (this.opensinglebookingcart == true) {
        // CRITICAL: Use the venue ID from sessionStorage - this is the EXACT ID used for getcalendar
        // This ensures we use the same ID that worked correctly in getcalendar
        let singleVenueId = sessionStorage.getItem("selectedVenueUniqueId");
        
        // If not in sessionStorage, fall back to currentUniqueVenueId
        if (!singleVenueId) {
          singleVenueId = this.currentUniqueVenueId;
        }
        
        if (!singleVenueId) {
          this.$toast.error("No venue selected. Please select a venue first.", {
            position: "top-right",
          });
          return;
        }
        
        // Ensure it's a string (not array)
        if (Array.isArray(singleVenueId)) {
          singleVenueId = singleVenueId[0];
        }
        singleVenueId = String(singleVenueId).trim();
        
        // If somehow it contains multiple IDs (comma-separated), take first
        if (singleVenueId.includes(',')) {
          singleVenueId = singleVenueId.split(',')[0].trim();
        }
        
        // CRITICAL: Create a completely fresh array with ONLY this single string ID
        // Create new array from scratch - don't reference anything else
        const uniqueVenueIds = [String(singleVenueId).trim()];
        
        // Final validation - ensure exactly one string element
        if (uniqueVenueIds.length !== 1 || 
            typeof uniqueVenueIds[0] !== 'string' || 
            uniqueVenueIds[0] === '' ||
            uniqueVenueIds[0].includes(',')) {
          this.$toast.error("Invalid venue ID. Please select a venue again.", {
            position: "top-right",
          });
          return;
        }
        
        const results = await search({
          StartDate: this.storeaddedbookingdetails[0].StartTime,
          EndDate: this.storeaddedbookingdetails[0].EndTime,
          UniqueVenueIds: uniqueVenueIds,
        });

        for (let index = 0; index < results?.length; index++) {
          if (results[index].isBooked == true) {
            this.isBooked = true;
          }
        }

        try {
          if (this.storeaddedbookingdetails.length > 0 && !this.isBooked) {
            this.$store.commit(
              `${ENV.VUE_APP_FACILITY}_booking/setTemporarybookingdetails`,
              this.storeaddedbookingdetails
            );
            const selectedDetails = this.storeaddedbookingdetails.filter(
              (element) => element.venueName === this.selectedVenueName
            );

            var res = {};
            // Check if bundle venue based on stored venueData
            const isBundle = selectedDetails[0].venueData?.bundleVenueIds && 
                            Array.isArray(selectedDetails[0].venueData.bundleVenueIds) && 
                            selectedDetails[0].venueData.bundleVenueIds.length > 0;
            
            // Use the venue ID from sessionStorage - this is the EXACT ID used for getcalendar
            let finalVenueId = sessionStorage.getItem("selectedVenueUniqueId");
            
            // If not in sessionStorage, fall back to currentUniqueVenueId
            if (!finalVenueId) {
              finalVenueId = this.currentUniqueVenueId;
            }
            
            if (!finalVenueId) {
              this.$toast.error("No venue selected. Please select a venue first.", {
                position: "top-right",
              });
              return;
            }
            
            // Ensure finalVenueId is a string (not array)
            if (Array.isArray(finalVenueId)) {
              finalVenueId = finalVenueId[0];
            }
            finalVenueId = String(finalVenueId).trim();
            
            // If somehow it contains multiple IDs (comma-separated), take first
            if (finalVenueId.includes(',')) {
              finalVenueId = finalVenueId.split(',')[0].trim();
            }
            
            if (isBundle) {
              // Build bundle-specific payload with correct venue ID (selected enabled venue)
              const bundlePayload = [{
                StartTime: selectedDetails[0].StartTime,
                EndTime: selectedDetails[0].EndTime,
                UniqueVenueId: finalVenueId,
              }];
              res = await setBundleSearchSelect(bundlePayload);
            } else {
              // Build regular payload for single venues - use correct venue ID
              const payload = selectedDetails.map((detail) => ({
                StartTime: detail.StartTime,
                EndTime: detail.EndTime,
                maxPeople: detail.maxPeople,
                UniqueVenueId: finalVenueId,
                venueName: detail.venueName,
                venueType: this.selectedvenueType,
              }));
              res = await setBookingSearchSelect(payload);
            }

            if (res.Status == "SEARCHING") {
              // Need to provide array instead of 0th index venueblock id
              sessionStorage.setItem(
                "venueBlockId",
                JSON.stringify(res.venueblockids)
              );
              // Store original user-selected times (without setdown time) for single bookings
              let originalStartTime = selectedDetails[0].displayStartTime || selectedDetails[0].StartTime;
              let originalEndTime = selectedDetails[0].displayEndTime || selectedDetails[0].EndTime;
              
              // If displayEndTime is missing and setdown time was applied, calculate original by subtracting setdown
              if (!selectedDetails[0].displayEndTime && this.checksetdownTime && this.setdownTime && this.setdownTime !== "00:00" && selectedDetails[0].EndTime) {
                const endMoment = moment(selectedDetails[0].EndTime, "DD/MM/YYYY HH:mm");
                const setdownDuration = moment.duration(this.setdownTime);
                originalEndTime = endMoment.clone().subtract(setdownDuration).format("DD/MM/YYYY HH:mm");
              }
              
              sessionStorage.setItem("starttime", originalStartTime);
              sessionStorage.setItem("endtime", originalEndTime);
              sessionStorage.setItem(
                "selectedvenuename",
                this.selectedVenueName
              );
              sessionStorage.setItem("statusbar", this.percentagebar + 14);
              // sessionStorage.setItem("price", this.fulldayChecked ? this.profitfulldayyprice : this.profithourlyprice)
              sessionStorage.setItem("description", this.description);
              sessionStorage.setItem("isFulldayBooking", this.fulldayChecked);
              sessionStorage.setItem("isHourlyBooking", this.hourlyChecked);
              sessionStorage.setItem("isFreeBooking", this.freebookingChecked);
              let venuetype = decodeURIComponent(
                location.search.split("venueType=")[1].replace(/_/g, " ")
              );
              let newVenueType = venuetype.replaceAll(" ", "_");
              let path = `/bookingapp/${ENV.VUE_APP_FACILITY}/details/`;
              this.$router.push({
                path: path,
                query: { venueType: newVenueType },
              });
            }
          } else {
            this.$toast.error(
              "This venue is not available at this date and time.",
              {
                position: "top-right",
              }
            );
          }
        } catch (error) {
          this.$toast.warning(error, { position: "top-right" });
        }
      } else {
        this.$store.commit(
          `${ENV.VUE_APP_FACILITY}_booking/setTemporarybookingdetails`,
          this.storemultiBookingCartData
        );

        // Get the UniqueVenueId from sessionStorage (same one used for getcalendar)
        let uniqueVenueId = sessionStorage.getItem("selectedVenueUniqueId");
        
        // If not in sessionStorage, try to get from first booking's uniqueVenueId or venueData
        if (!uniqueVenueId && this.storemultiBookingCartData.length > 0) {
          const firstBooking = this.storemultiBookingCartData[0];
          uniqueVenueId = firstBooking.uniqueVenueId || 
                         firstBooking.venueData?.id || 
                         this.currentUniqueVenueId;
        }
        
        // Fallback to currentUniqueVenueId if still not found
        if (!uniqueVenueId) {
          uniqueVenueId = this.currentUniqueVenueId;
        }
        
        if (!uniqueVenueId) {
          this.$toast.error("No venue selected. Please select a venue first.", {
            position: "top-right",
          });
          return;
        }
        
        // Ensure it's a string (not array)
        if (Array.isArray(uniqueVenueId)) {
          uniqueVenueId = uniqueVenueId[0];
        }
        uniqueVenueId = String(uniqueVenueId).trim();
        
        // If somehow it contains multiple IDs (comma-separated), take first
        if (uniqueVenueId.includes(',')) {
          uniqueVenueId = uniqueVenueId.split(',')[0].trim();
        }
        
        // Create array with single venue ID
        const uniqueVenueIds = [uniqueVenueId];

        // Check if any of the bookings are already booked
        for (let i = 0; i < this.storemultiBookingCartData.length; i++) {
          const results = await search({
            StartDate: this.storemultiBookingCartData[i].StartTime,
            EndDate: this.storemultiBookingCartData[i].EndTime,
            UniqueVenueIds: uniqueVenueIds,
          });
          
          // Check if results is valid and has the expected structure
          if (results && Array.isArray(results) && results.length > 0) {
            // Check each result in the array
            for (let j = 0; j < results.length; j++) {
              if (results[j] && results[j].isBooked == true) {
                this.isBooked = true;
                break; // No need to check further if one is booked
              }
            }
          } else if (results && results.isBooked == true) {
            // Handle case where results is a single object, not an array
            this.isBooked = true;
          }
        }

        try {
          if (this.storemultiBookingCartData.length > 0 && !this.isBooked) {
            // Normalize payload to match single booking format exactly
            // Only include: StartTime, EndTime, maxPeople, UniqueVenueId, venueName, venueType
            // IMPORTANT: Use displayStartTime and displayEndTime (original user-selected times)
            // instead of StartTime/EndTime (which include setup/setdown time adjustments)
            const normalizedPayload = this.storemultiBookingCartData.map(booking => ({
              StartTime: booking.displayStartTime || booking.StartTime,
              EndTime: booking.displayEndTime || booking.EndTime,
              maxPeople: String(booking.maxPeople || ''), // Ensure maxPeople is always a string
              UniqueVenueId: booking.uniqueVenueId || booking.venueData?.id || '',
              venueName: booking.venueName,
              venueType: booking.venueType
            }));
            
            const res = await setMultiBookingSearchSelect(
              normalizedPayload
            );
            
            // Check if response is valid and has the expected structure
            if (!res) {
              this.$toast.error("No response from server. Please try again.", {
                position: "top-right",
              });
              return;
            }
            
            // Check for Status field (case-insensitive) or status field
            const status = res.Status || res.status || res.STATUS;
            
            if (status == "SEARCHING" || status == "searching" || status == "SEARCHING") {
              // Need to provide array instead of 0th index venueblock id
              const venueBlockIds = res.venueblockids || res.venueBlockIds || res.venueblockid || [];
              
              if (!venueBlockIds || (Array.isArray(venueBlockIds) && venueBlockIds.length === 0)) {
                this.$toast.error("No venue block IDs received. Please try again.", {
                  position: "top-right",
                });
                return;
              }
              
              sessionStorage.setItem(
                "venueBlockId",
                JSON.stringify(venueBlockIds)
              );
              // Store original times (displayStartTime/displayEndTime) for summary page
              // These are the user-selected times without setdown time adjustments
              const firstBooking = this.storemultiBookingCartData[0];
              sessionStorage.setItem(
                "starttime",
                firstBooking.displayStartTime || firstBooking.StartTime
              );
              sessionStorage.setItem(
                "endtime",
                firstBooking.displayEndTime || firstBooking.EndTime
              );
              
              // Store all multi-booking original times for summary page
              const originalTimes = this.storemultiBookingCartData.map(booking => ({
                StartTime: booking.displayStartTime || booking.StartTime,
                EndTime: booking.displayEndTime || booking.EndTime,
                venueName: booking.venueName || this.selectedVenueName,
                uniqueVenueId: booking.uniqueVenueId || booking.venueData?.id || this.currentUniqueVenueId || '',
                VenueId: booking.VenueId || ''
              }));
              sessionStorage.setItem("multiBookingOriginalTimes", JSON.stringify(originalTimes));
              sessionStorage.setItem("statusbar", this.percentagebar + 14);
              sessionStorage.setItem(
                "price",
                this.fulldayChecked
                  ? this.profitfulldayyprice
                  : this.profithourlyprice
              );
              sessionStorage.setItem("isFulldayBooking", this.fulldayChecked);
              sessionStorage.setItem("isHourlyBooking", this.hourlyChecked);
              sessionStorage.setItem("isFreeBooking", this.freebookingChecked);
              
              // Set BookingType to multibooking
              sessionStorage.setItem("BookingType", "multibooking");
              
              let venuetype = "";
              try {
                venuetype = decodeURIComponent(
                location.search.split("venueType=")[1].replace(/_/g, " ")
              );
              } catch (e) {
                // Fallback if venueType is not in URL
                venuetype = this.selectedvenueType || "";
              }
              let newVenueType = venuetype.replaceAll(" ", "_");
              let path = `/bookingapp/${ENV.VUE_APP_FACILITY}/details/`;
              
              this.$router.push({
                path: path,
                query: { venueType: newVenueType },
              });
            } else {
              // Status is not SEARCHING - show error with actual status
              const errorMessage = res.message || res.Message || `Unexpected status: ${status}`;
              this.$toast.error(errorMessage || "Booking search failed. Please try again.", {
                position: "top-right",
              });
              console.error('Unexpected status in response:', status, res);
            }
          } else {
            if (this.storemultiBookingCartData.length === 0) {
            this.$toast.warning("No bookings added!!", {
              position: "top-right",
            });
            } else if (this.isBooked) {
              this.$toast.error("One or more venues are already booked at the selected time.", {
              position: "top-right",
            });
            }
          }
        } catch (error) {
          console.error('Error in multi-booking book method:', error);
          this.$toast.error(error?.message || "An error occurred. Please try again.", {
            position: "top-right",
          });
        }
      }
      /* eslint-enable no-unreachable */
    },

    DifferenceHours(start, end, action, venuename) {
      const startTime = moment(start, "DD/MM/YYYY HH:mm");
      const endTime = moment(end, "DD/MM/YYYY HH:mm");
      let minutes = endTime.diff(startTime, "minutes");
      let hours = minutes / 60;
      let getHours = sessionStorage.getItem(venuename);
      if (getHours != null) {
        if (action === "add") {
          hours = parseFloat(hours) + parseFloat(getHours);
          sessionStorage.setItem(venuename, hours);
        } else if (action === "delete") {
          hours = parseFloat(getHours) - parseFloat(hours);
          sessionStorage.setItem(venuename, hours);
        }
      } else {
        sessionStorage.setItem(venuename, hours);
      }
    },

    addSetupSetDownTime(
      startDateStr,
      endDateStr,
      addonStartTimeStr,
      addonEndTimeStr
    ) {
      var startDate = moment(startDateStr, "DD/MM/YYYY HH:mm");
      var endDate = moment(endDateStr, "DD/MM/YYYY HH:mm");
      var addonStartTime = moment.duration(addonStartTimeStr);
      var addonEndTime = moment.duration(addonEndTimeStr);

      // CORRECT LOGIC:
      // Setup time should be subtracted from start time (to allow time for setup before booking starts)
      // Setdown time should be added to end time (to allow time for cleanup after booking ends)
      startDate.subtract(addonStartTime);  // Subtract setup time from start
      endDate.add(addonEndTime);           // Add setdown time to end

      var formattedStartDate = startDate.format("DD/MM/YYYY HH:mm");
      var formattedEndDate = endDate.format("DD/MM/YYYY HH:mm");

      return [formattedStartDate, formattedEndDate];
    },

    async setselectedBookingList(selectedvenue) {
      let slotAdded = false;

      try {
      // Store original times before any modifications
      const originalStartTime = selectedvenue.StartTime;
      const originalEndTime = selectedvenue.EndTime;
      
      if (this.fulldayChecked) {
        // Full-day: use times from payload (venue's working hours from API, set by DateSearchComponent)
        selectedvenue.displayStartTime = selectedvenue.StartTime || originalStartTime;
        selectedvenue.displayEndTime = selectedvenue.EndTime || originalEndTime;
      } else {
        let formattedStartTime = originalStartTime;
        let formattedEndTime = originalEndTime;
        
        // Apply setup time only if setdown time is also configured (setup/setdown is relevant)
        if (this.checksetupTime && this.setupTime && this.setupTime !== "00:00" && this.setupTime !== "" && this.setdownTime && this.setdownTime !== "00:00") {
          const startMoment = moment(originalStartTime, "DD/MM/YYYY HH:mm");
          const setupDuration = moment.duration(this.setupTime);
          formattedStartTime = startMoment.clone().subtract(setupDuration).format("DD/MM/YYYY HH:mm");
        }
        
        // Apply setdown time if configured
        if (this.checksetdownTime && this.setdownTime && this.setdownTime !== "00:00" && this.setdownTime !== "") {
          const endMoment = moment(originalEndTime, "DD/MM/YYYY HH:mm");
          const setdownDuration = moment.duration(this.setdownTime);
          formattedEndTime = endMoment.clone().add(setdownDuration).format("DD/MM/YYYY HH:mm");
        }
        
        // Store original times for display (user-selected times without adjustments)
        selectedvenue.displayStartTime = originalStartTime;
        selectedvenue.displayEndTime = originalEndTime;
        
        // Store original times for display
        selectedvenue.displayStartTime = originalStartTime;
        selectedvenue.displayEndTime = originalEndTime;
        
        this.DifferenceHours(
          selectedvenue.StartTime,
          selectedvenue.EndTime,
          "add",
          this.selectedVenueName
        );
        
        // Update StartTime/EndTime for API calls (with setup/setdown adjustments)
        if (this.checksetupTime && this.setupTime && this.setupTime !== "00:00" && this.setupTime !== "") {
          selectedvenue.StartTime = formattedStartTime;
        }
        if (this.checksetdownTime && this.setdownTime && this.setdownTime !== "00:00" && this.setdownTime !== "") {
          selectedvenue.EndTime = formattedEndTime;
        }
      }
      
      // Add unique venue ID from new API data (stored in component variable)
      // NOTE: API returns lowercase field names (venuename, not venueName)
      if (this.venuesFromNewAPI && Array.isArray(this.venuesFromNewAPI)) {
        const currentVenueData = this.venuesFromNewAPI.find(v => 
          (v.venuename || v.venueName) === this.selectedVenueName
        );
        
        if (currentVenueData && currentVenueData.id) {
          // Ensure id is always a string - this is the "FCC-VEN-XX" format ID
          selectedvenue.uniqueVenueId = String(currentVenueData.id);
          
          // Also store complete venue data for reference (using lowercase field names)
          selectedvenue.venueData = {
            id: String(currentVenueData.id),  // Main venue identifier like "FCC-VEN-93"
            venueID: currentVenueData.venueid || currentVenueData.venueID,
            bundleVenueIds: currentVenueData.bundlevenueids || currentVenueData.bundleVenueIds,
          };
          
        } else if (process.env.NODE_ENV !== "production") {
          console.warn('No venue ID found for:', this.selectedVenueName, 'Data:', currentVenueData);
        }
      }

      const clonedVenue = JSON.parse(JSON.stringify(selectedvenue));

      const currentBookingType =
        sessionStorage.getItem("BookingType") ||
        (this.multiBookingFlowActive ? "multibooking" : "singlebooking");

      if (currentBookingType === "multibooking" && this.multiBookingActive) {
        this.multiBookingFlowActive = true;
        this.openmultibookingcart = true;

        // Ensure venue metadata is present for display
        clonedVenue.venueName = clonedVenue.venueName || this.selectedVenueName;
        clonedVenue.venueType = clonedVenue.venueType || this.selectedvenueType;

        // Validate and process the booking (check duplicates and consolidate consecutive)
        const validationResult = this.validateAndProcessMultiBooking(clonedVenue);
        
        if (!validationResult.success) {
          // Show error message if validation failed
          this.$toast.error(validationResult.message, {
            position: "top-right",
          });
          return;
        }

        // Update cart data (may have been consolidated)
        this.storemultiBookingCartData = validationResult.updatedCart;
        await this.setMultiBookingData(this.storemultiBookingCartData);
        this.$store.commit(
          `${ENV.VUE_APP_FACILITY}_booking/setMultibookingdetails`,
          this.storemultiBookingCartData
        );
        
        // Show success message if bookings were consolidated
        if (validationResult.consolidated) {
          this.$toast.success(validationResult.message, {
            position: "top-right",
          });
        }
        
        this.selectdatetime = false;
        this.updateSingleBookingButtonState();
        slotAdded = true;
        return;
      }

      this.storeaddedbookingdetails.push(clonedVenue);
      if (this.multiBookingActive === false) {
        if (this.storeaddedbookingdetails.length >= 1) {
          const foundBooking = this.storeaddedbookingdetails.filter(
            (element) => element.venueName === this.selectedVenueName
          );
          if (foundBooking.length > 0) {
            this.singleBookingActive = false;
          } else {
            this.singleBookingActive = true;
          }
        }
      }
      await this.setTempBooking(this.storeaddedbookingdetails);
      this.$store.commit(
        `${ENV.VUE_APP_FACILITY}_booking/temporarybookingdetails`,
        this.storeaddedbookingdetails
      );
      this.selectdatetime = false;
      slotAdded = true;
      } finally {
        if (slotAdded) {
          await this.scrollToWizardSessionCard();
        }
        this.setWizardLoading(false);
        this.updateScrollDownHint();
      }
    },
    onSlotAddStart() {
      this.setWizardLoading(true, "Adding slot…");
    },
    onSlotAddEnd() {
      this.setWizardLoading(false);
    },
    async scrollToWizardSessionCard() {
      await this.$nextTick();
      await this.$nextTick();
      await new Promise((resolve) => setTimeout(resolve, 220));

      const node =
        document.querySelector("#wizard-step-2 .booking-wizard__card--slot") ||
        document.getElementById("wizard-step-2") ||
        this.$refs.wizardSessionCard?.$el ||
        this.$refs.wizardSessionCard;

      if (node?.getBoundingClientRect) {
        scrollElementIntoView(node, { block: "center", behavior: "smooth" });
      }
    },
    updateScrollDownHint() {
      if (this.wizardStep < 3 || this.wizardLoading) {
        this.showScrollDownHint = false;
        return;
      }

      this.showScrollDownHint = this.isWizardContinueHintNeeded();
    },
    isWizardContinueHintNeeded() {
      const viewportHeight = window.innerHeight;
      const headerOffset = 80;
      const activeStep = this.resolveWizardStepNode(this.wizardStep);
      const stepHead = activeStep?.querySelector?.(".booking-wizard__step-head");
      const nav = document.getElementById("wizard-step-nav");

      let headVisible = false;
      if (stepHead?.getBoundingClientRect) {
        const rect = stepHead.getBoundingClientRect();
        headVisible = rect.bottom > headerOffset && rect.top < viewportHeight - 32;
      }

      let navVisible = false;
      if (nav?.getBoundingClientRect) {
        const rect = nav.getBoundingClientRect();
        navVisible = rect.top < viewportHeight - 48 && rect.bottom > headerOffset;
      }

      if (headVisible || navVisible) {
        return false;
      }

      let headBelowFold = false;
      if (stepHead?.getBoundingClientRect) {
        headBelowFold = stepHead.getBoundingClientRect().top > viewportHeight - 120;
      }

      let navBelowFold = false;
      if (nav?.getBoundingClientRect) {
        navBelowFold = nav.getBoundingClientRect().top > viewportHeight - 64;
      }

      return headBelowFold || navBelowFold;
    },
    scrollToContinueWizard() {
      const activeStep = this.resolveWizardStepNode(this.wizardStep);
      const node =
        activeStep?.querySelector?.(".booking-wizard__step-head") || activeStep;

      if (!node) {
        return;
      }

      scrollElementIntoView(node, { block: "center", behavior: "smooth" });

      window.setTimeout(() => {
        scrollElementIntoView(node, { block: "center", behavior: "smooth" });
        this.updateScrollDownHint();
      }, 480);

      window.setTimeout(() => this.updateScrollDownHint(), 900);
    },
    clearWizardSession() {
      this.storeaddedbookingdetails = [];
      this.storemultiBookingCartData = [];
      this.openmultibookingcart = false;
      this.opensinglebookingcart = true;
      this.singleBookingActive = true;
      this.multiBookingFlowActive = false;
      this.wizardSlotReserved = false;
      this.wizardDetailsSubmitted = false;
      this.wizardCalculatedAmount = 0;
      this.wizardPaymentRate = 0;
      this.wizardStoreVenueBlockId = null;
      sessionStorage.removeItem("venueBlockId");
      this.setTempBooking([]);
      this.setMultiBookingData([]);
      this.$store.commit(
        `${ENV.VUE_APP_FACILITY}_booking/temporarybookingdetails`,
        []
      );
      this.$store.commit(
        `${ENV.VUE_APP_FACILITY}_booking/setMultibookingdetails`,
        []
      );
      if (this.wizardStep === 2) {
        this.selectdatetime = true;
        this.enterWizardStepTwo();
      }
    },
    getWizardStepElementId(step) {
      if (step === this.wizardExtraStep) return "wizard-step-extra";
      if (step === this.wizardReviewStep) return "wizard-step-review";
      return `wizard-step-${step}`;
    },
    resolveWizardStepNode(step) {
      const id = this.getWizardStepElementId(step);
      const byId = document.getElementById(id);
      if (byId) return byId;

      let refName = `wizardStep${step}`;
      if (step === this.wizardExtraStep) {
        refName = "wizardStepExtra";
      } else if (step === this.wizardReviewStep) {
        refName = "wizardStepReview";
      }

      const ref = this.$refs[refName];
      const el = Array.isArray(ref) ? ref[0] : ref;
      return el?.$el ?? el ?? null;
    },
    async scrollToWizardStep(step, attempt = 0) {
      await this.$nextTick();
      await this.$nextTick();

      if (attempt === 0) {
        await new Promise((resolve) => setTimeout(resolve, 150));
      }

      const stepId = this.getWizardStepElementId(step);
      let node =
        document.querySelector(`#${stepId} .booking-wizard__step-head`) ||
        document.getElementById(stepId) ||
        this.resolveWizardStepNode(step);

      if (step === this.wizardReviewStep) {
        node =
          document.querySelector("#wizard-step-review .booking-wizard__step-head") ||
          document.getElementById("wizard-step-nav") ||
          node;
      }

      if (!node?.getBoundingClientRect) {
        if (attempt < 12) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          return this.scrollToWizardStep(step, attempt + 1);
        }
        return;
      }

      scrollElementIntoView(node, { offset: 88 });

      window.setTimeout(() => {
        scrollElementIntoView(node, { offset: 88 });
      }, 320);
    },
    enterWizardStepTwo() {
      if (this.hasBookingInCart) {
        this.selectdatetime = false;
        return;
      }
      if (!this.multiBookingFlowActive) {
        this.booksinglebooking();
      } else {
        this.selectdatetime = true;
      }
    },
    async wizardNext() {
      if (this.wizardStep === 1 && this.selectedVenueName) {
        this.setWizardLoading(true, "Loading session options…");
        try {
          this.wizardStep = 2;
          this.enterWizardStepTwo();
        } finally {
          this.setWizardLoading(false);
        }
        await this.scrollToWizardStep(2);
        return;
      }
      if (this.wizardStep === 2 && this.hasBookingInCart) {
        await this.wizardNextFromSlot();
        return;
      }
      if (this.wizardStep === 3) {
        await this.wizardNextFromContact();
        return;
      }
      if (
        this.wizardStep === this.wizardExtraStep &&
        this.showSecondDetailStep
      ) {
        await this.wizardNextFromSecondDetail();
      }
    },
    async wizardNextFromSlot() {
      if (!this.hasBookingInCart) return;
      if (this.isSessionExpired()) {
        this.sessionTimerExpired = true;
        return;
      }

      this.setWizardLoading(true, "Reserving your session…");
      let scrollTarget = null;

      try {
        if (
          !this.opensinglebookingcart &&
          this.storeaddedbookingdetails?.length > 0
        ) {
          this.opensinglebookingcart = true;
        }

        const reserved = await this.reserveBookingSlot();
        if (!reserved) return;

        this.wizardSlotReserved = true;
        this.wizardStep = 3;
        scrollTarget = 3;
      } finally {
        this.setWizardLoading(false);
      }

      if (scrollTarget != null) {
        await this.scrollToWizardStep(scrollTarget);
      }
    },
    setWizardLoading(active, message = "Preparing your booking…") {
      this.wizardLoading = active;
      if (message) {
        this.wizardLoadingMessage = message;
      }
      if (!active) {
        this.$nextTick(() => this.updateScrollDownHint());
      }
    },
    async wizardNextFromContact() {
      if (!this.contactStepValid) {
        this.$toast.error("Please complete all required contact fields.", {
          position: "top-right",
        });
        return;
      }
      if (this.isSessionExpired()) {
        this.sessionTimerExpired = true;
        return;
      }

      const contactStep = this.$refs.contactStep;
      if (!contactStep) {
        this.$toast.error("Please complete contact information.", {
          position: "top-right",
        });
        return;
      }

      this.wizardPaymentError = "";
      let scrollTarget = null;

      this.setWizardLoading(true, "Saving contact details…");

      try {
        const contact = await contactStep.validateAndGetValues();
        if (!contact) {
          this.$toast.error("Please complete all required contact fields.", {
            position: "top-right",
          });
          return;
        }

        this.wizardContactDetails = contact;
        sessionStorage.setItem("bookingWizardContact", JSON.stringify(contact));

        if (this.showWizardExtraStep) {
          this.wizardStep = this.wizardExtraStep;
          scrollTarget = this.wizardExtraStep;
        } else {
          this.setWizardLoading(true, "Submitting your booking details…");
          this.wizardPricingLoading = true;

          const submitted = await this.submitWizardBookingDetail(contact);
          if (!submitted) return;

          this.setWizardLoading(true, "Calculating your booking amount…");
          await this.fetchWizardSummaryPrice();
          this.wizardStep = this.wizardReviewStep;
          scrollTarget = this.wizardReviewStep;
        }
      } finally {
        this.setWizardLoading(false);
        this.wizardPricingLoading = false;
      }

      if (scrollTarget != null) {
        await this.scrollToWizardStep(scrollTarget);
        if (
          scrollTarget === this.wizardExtraStep &&
          this.showOpenFormsStep &&
          this.$refs.openFormsStep
        ) {
          this.$refs.openFormsStep.initialize();
        }
      }
    },
    async wizardNextFromSecondDetail() {
      if (this.isSessionExpired()) {
        this.sessionTimerExpired = true;
        return;
      }

      const contact = this.wizardContactDetails;
      if (!contact?.FirstName) {
        this.$toast.error("Contact details are missing.", {
          position: "top-right",
        });
        return;
      }

      const step = this.$refs.secondDetailStep;
      if (!step) return;

      this.setWizardLoading(true, "Submitting your booking details…");
      this.wizardPricingLoading = true;
      this.wizardPaymentError = "";
      let scrollTarget = null;

      try {
        const result = step.validateAndBuild();
        if (!result) return;

        if (result.nonOverlapAddon?.AddonName?.length > 0) {
          const checkAddon = await checkOverlapping(result.nonOverlapAddon);
          if (checkAddon?.status === true) {
            this.$toast.error(
              "Addon already booked for the same date and time",
              { position: "top-right" }
            );
            return;
          }
        }

        this.wizardSecondPageDetails = result.secondPageDetails;
        this.wizardDetailQuestions = result.detailQuestions;

        const submitted = await this.submitWizardBookingDetail(
          contact,
          result.secondPageDetails
        );
        if (!submitted) return;

        const isFree = sessionStorage.getItem("isFreeBooking") === "true";
        if (
          !isFree &&
          this.disableORenable2ndDetailPage &&
          (result.overlapAddon?.AddonName?.length > 0 ||
            result.nonOverlapAddon?.AddonName?.length > 0)
        ) {
          this.setWizardLoading(true, "Updating add-on facilities…");
          await addAddonFacility(result.overlapAddon, result.nonOverlapAddon);
          if (result.addonRateIncrease > 0) {
            this.wizardPaymentRate =
              (this.wizardPaymentRate || 0) + result.addonRateIncrease;
            this.wizardCalculatedAmount = this.wizardPaymentRate;
          }
        }

        this.setWizardLoading(true, "Calculating your booking amount…");
        await this.fetchWizardSummaryPrice();
        this.wizardStep = this.wizardReviewStep;
        scrollTarget = this.wizardReviewStep;
      } finally {
        this.setWizardLoading(false);
        this.wizardPricingLoading = false;
      }

      if (scrollTarget != null) {
        await this.scrollToWizardStep(scrollTarget);
      }
    },
    async handleOpenFormsSubmitted() {
      if (this.openFormsProcessing) return;
      this.openFormsProcessing = true;
      this.setWizardLoading(true, "Submitting your booking details…");
      this.wizardPricingLoading = true;
      this.wizardPaymentError = "";
      let scrollTarget = null;

      try {
        const contact = this.wizardContactDetails;
        if (!contact?.FirstName) {
          this.$toast.error("Contact details are missing.", {
            position: "top-right",
          });
          return;
        }

        const submitted = await this.submitWizardBookingDetail(contact, []);
        if (!submitted) return;

        this.setWizardLoading(true, "Calculating your booking amount…");
        await this.fetchWizardSummaryPrice();
        this.wizardStep = this.wizardReviewStep;
        scrollTarget = this.wizardReviewStep;
      } finally {
        this.openFormsProcessing = false;
        this.setWizardLoading(false);
        this.wizardPricingLoading = false;
      }

      if (scrollTarget != null) {
        await this.scrollToWizardStep(scrollTarget);
      }
    },
    async loadWizardDetailSettings() {
      try {
        const settingsResponse = getBookingDetailSettings();
        this.detailsSettings =
          settingsResponse?.[0]?.detailSettingSection || [];

        this.disableORenable2ndDetailPage = false;
        this.isEmbededOpenFormEnable = false;
        this.embededUrl = "";

        for (const element of this.detailsSettings) {
          if (element.venuetype === this.selectedvenueType) {
            this.disableORenable2ndDetailPage = element.isDetailPageEnabled;
            this.isEmbededOpenFormEnable = !!element.isEmbededOpenFormEnable;
            this.embededUrl = element.embededUrl || "";
          }
        }

        this.facilityAddonDetails = [
          { title: "Overlap", data: [] },
          { title: "Non-Overlap", data: [] },
        ];
        this.facilityAddonTemp = [];
        this.enableOverlap = false;
        this.enableNonOverlap = false;

        const addonDetails = await GetPaymentSettings();
        for (const element of addonDetails?.data || []) {
          this.facilityAddonTemp.push(element);
          if (element.venuetype !== this.selectedvenueType) continue;

          if (element.addontype === "Overlap" && element.enablefacility) {
            this.enableOverlap = true;
            const overlapIndex = this.facilityAddonDetails.findIndex(
              (item) => item.title === "Overlap"
            );
            if (overlapIndex !== -1) {
              this.facilityAddonDetails[overlapIndex].data.push(element);
            }
          }
          if (element.addontype === "Non-Overlap" && element.enablefacility) {
            this.enableNonOverlap = true;
            const nonOverlapIndex = this.facilityAddonDetails.findIndex(
              (item) => item.title === "Non-Overlap"
            );
            if (nonOverlapIndex !== -1) {
              this.facilityAddonDetails[nonOverlapIndex].data.push(element);
            }
          }
        }
      } catch {
        // detail settings optional for venues without second page
      }
    },
    async saveWizardUploadedDocuments(detailQuestions) {
      const uploadFiles = (detailQuestions || []).filter(
        (item) => item.file && item.file.file
      );
      if (!uploadFiles.length) return;

      let venueblockid;
      try {
        venueblockid = JSON.parse(sessionStorage.getItem("venueBlockId"));
      } catch {
        return;
      }
      const blockId = Array.isArray(venueblockid)
        ? venueblockid[0]
        : venueblockid;

      for (const image of uploadFiles) {
        const formData = new FormData();
        formData.append("file", image.file.file);
        try {
          await SaveImage(formData, blockId);
        } catch {
          this.$toast.error("Error while uploading image", {
            position: "top-right",
            duration: 5000,
          });
        }
      }
    },
    wizardBack() {
      if (this.wizardStep > 1) {
        if (this.wizardStep === 3) {
          this.wizardPaymentError = "";
        }
        if (this.wizardStep === this.wizardReviewStep) {
          this.wizardPaymentError = "";
          this.wizardDetailsSubmitted = false;
        }
        if (this.wizardStep === this.wizardExtraStep) {
          this.wizardSecondPageDetails = [];
          this.wizardDetailQuestions = [];
        }
        this.wizardStep -= 1;
        if (this.wizardStep === 1) {
          this.selectdatetime = false;
        } else if (this.wizardStep === 2) {
          this.enterWizardStepTwo();
        }
        void this.scrollToWizardStep(this.wizardStep);
        return;
      }
      this.navigateHome();
    },
    async confirmAndPay() {
      this.wizardPaymentError = "";
      if (this.isSessionExpired()) {
        this.sessionTimerExpired = true;
        return;
      }

      if (!this.wizardContactDetails?.FirstName) {
        const saved = sessionStorage.getItem("bookingWizardContact");
        if (saved) {
          try {
            this.wizardContactDetails = JSON.parse(saved);
          } catch {
            this.wizardContactDetails = {};
          }
        }
      }

      const contact = this.wizardContactDetails;
      if (!contact?.FirstName || !contact?.Email) {
        this.$toast.error("Contact details are missing. Please go back and complete step 3.", {
          position: "top-right",
        });
        return;
      }

      if (!this.hasBookingInCart) {
        this.$toast.error("Please add a session before confirming.", {
          position: "top-right",
        });
        return;
      }

      if (!this.wizardSlotReserved) {
        this.$toast.error("Session reservation expired. Please go back to step 2.", {
          position: "top-right",
        });
        return;
      }

      this.confirmInProgress = true;
      this.setWizardLoading(true, "Preparing secure payment…");

      let redirectingToPayment = false;

      try {
        const isOtpUser = this.readOnlyOTP || sessionStorage.getItem("readOnlyOTP") === "true";
        if (isOtpUser) {
          const booking =
            this.wizardSessionBooking || this.storeaddedbookingdetails?.[0];
          const hoursNeeded = getWizardSessionDurationHours(booking);
          const remaining = getOtpBookingHoursRemaining();

          if (remaining === 0) {
            this.$toast.error("You have no booking hours remaining.", {
              position: "top-right",
            });
            return;
          }

          if (hoursNeeded > remaining) {
            this.$toast.error(
              `Insufficient hours remaining. This slot needs ${hoursNeeded.toFixed(2)} hour(s), but you have ${remaining} hour(s) left.`,
              { position: "top-right" }
            );
            return;
          }
        }

        if (!this.wizardDetailsSubmitted) {
          this.setWizardLoading(true, "Submitting your booking details…");
          const submitted = await this.submitWizardBookingDetail(
            contact,
            this.wizardSecondPageDetails
          );
          if (!submitted) return;
          await this.fetchWizardSummaryPrice();
        }

        if (isOtpUser) {
          this.setWizardLoading(true, "Confirming your complimentary booking…");
          if (this.wizardDetailQuestions?.length) {
            await this.saveWizardUploadedDocuments(this.wizardDetailQuestions);
          }

          sessionStorage.setItem(
            "otpBookingHoursUsed",
            String(
              getWizardSessionDurationHours(
                this.wizardSessionBooking || this.storeaddedbookingdetails?.[0]
              )
            )
          );
          markOtpBookingCheckout();
          sessionStorage.setItem("wizardPaymentStarted", "1");
          this.resetWizardBookingState({ keepVenue: true, keepReservation: true });
          this.$router.push({
            path: `/bookingapp/${ENV.VUE_APP_FACILITY}/summarypage/`,
          });
          return;
        }

        const isFree = sessionStorage.getItem("isFreeBooking") === "true";
        if (isFree) {
          this.setWizardLoading(true, "Confirming your booking…");
          if (this.wizardDetailQuestions?.length) {
            await this.saveWizardUploadedDocuments(this.wizardDetailQuestions);
          }
          sessionStorage.setItem("wizardPaymentStarted", "1");
          this.resetWizardBookingState({ keepVenue: true, keepReservation: true });
          this.$router.push({
            path: `/bookingapp/${ENV.VUE_APP_FACILITY}/summarypage/`,
          });
          return;
        }

        const paymentStarted = await this.processWizardPayment(contact);
        if (paymentStarted) {
          redirectingToPayment = true;
          this.setWizardLoading(true, "Redirecting to secure payment…");
        }
      } finally {
        this.confirmInProgress = false;
        if (!redirectingToPayment) {
          this.setWizardLoading(false);
        }
      }
    },
    async fetchWizardSummaryPrice() {
      if (this.readOnlyOTP || sessionStorage.getItem("readOnlyOTP") === "true") {
        this.wizardCalculatedAmount = 0;
        this.wizardPaymentRate = 0;
        return;
      }

      if (this.freebookingChecked) {
        this.wizardCalculatedAmount = 0;
        this.wizardPaymentRate = 0;
        return;
      }

      if (this.wizardPaymentRate > 0) {
        this.wizardCalculatedAmount = this.wizardPaymentRate;
        return;
      }

      this.wizardPricingLoading = true;
      try {
        let blockId = null;
        try {
          const raw = sessionStorage.getItem("venueBlockId");
          const parsed = raw ? JSON.parse(raw) : null;
          blockId = Array.isArray(parsed) ? parsed[0] : parsed;
        } catch {
          blockId = null;
        }

        if (blockId) {
          const pricing = await getPricingDetail(blockId);
          const amount =
            pricing?.totalPrice ??
            pricing?.fulldayPrice ??
            pricing?.price ??
            pricing?.amount ??
            0;
          if (amount) {
            this.wizardCalculatedAmount = Number(amount);
            this.wizardPaymentRate = Number(amount);
            return;
          }
        }

        const selectedDetails = (this.storeaddedbookingdetails || []).filter(
          (element) => element.venueName === this.selectedVenueName
        );
        if (selectedDetails.length) {
          let finalVenueId = sessionStorage.getItem("selectedVenueUniqueId");
          if (!finalVenueId) finalVenueId = this.currentUniqueVenueId;
          const payload = selectedDetails.map((detail) => ({
            StartTime: detail.StartTime,
            EndTime: detail.EndTime,
            maxPeople: detail.maxPeople,
            UniqueVenueId: String(finalVenueId || "").trim(),
            venueName: detail.venueName,
            venueType: this.selectedvenueType,
          }));
          const priceData = await getPriceBookingPage(payload);
          const amount =
            priceData?.totalPrice ??
            priceData?.fulldayPrice ??
            priceData?.price ??
            priceData?.amount ??
            0;
          if (amount) {
            this.wizardCalculatedAmount = Number(amount);
            this.wizardPaymentRate = Number(amount);
          }
        }
      } catch {
        // pricing optional fallback; setBookingDetail amount used when available
      } finally {
        this.wizardPricingLoading = false;
      }
    },
    async processWizardPayment(contact) {
      this.wizardPaymentError = "";

      this.setWizardLoading(true, "Connecting to payment gateway…");
      const authKey = await generateAuthKeys();
      if (
        !authKey?.data ||
        authKey.data === null ||
        !authKey.data.data ||
        authKey.data.data === ""
      ) {
        this.wizardPaymentError =
          "Error while processing payment, please try again later.";
        this.$toast.error(this.wizardPaymentError, { position: "top-right" });
        return false;
      }

      const crn2 = sessionStorage.getItem("crn2") || "";
      if (!crn2) {
        this.wizardPaymentError =
          "Booking is not possible at this time, please try again.";
        this.$toast.error(this.wizardPaymentError, { position: "top-right" });
        return false;
      }

      const finalPrice = this.wizardPaymentRate || this.wizardCalculatedAmount;
      if (!finalPrice && !this.freebookingChecked) {
        this.wizardPaymentError = "Unable to determine booking amount.";
        this.$toast.error(this.wizardPaymentError, { position: "top-right" });
        return false;
      }

      const venueId =
        sessionStorage.getItem("selectedVenueUniqueId") ||
        this.wizardSessionBooking?.uniqueVenueId ||
        "";

      let venueBlockIdForAttach = this.wizardStoreVenueBlockId;
      if (venueBlockIdForAttach == null || venueBlockIdForAttach === "") {
        try {
          const raw = sessionStorage.getItem("venueBlockId");
          if (raw) {
            const parsed = JSON.parse(raw);
            venueBlockIdForAttach = Array.isArray(parsed) ? parsed[0] : parsed;
          }
        } catch {
          venueBlockIdForAttach = null;
        }
      }

      try {
        this.setWizardLoading(true, "Preparing your payment…");
        const res = await attachTransactionDetail(
          authKey.data.data,
          finalPrice,
          crn2,
          venueId,
          contact.FirstName,
          contact.LastName,
          contact.Email,
          venueBlockIdForAttach
        );

        if (res.data.status !== 1) {
          this.wizardPaymentError =
            "Something went wrong while preparing payment. Please try again.";
          this.$toast.error(this.wizardPaymentError, { position: "top-right" });
          return false;
        }

        this.setWizardLoading(true, "Opening secure payment page…");
        const response = await hppConfiguration(authKey.data.data);
        const responseData =
          response.data.payment_url || response.data.response;

        if (response.data.status !== 1) {
          this.wizardPaymentError = "Payment configuration failed.";
          this.$toast.error(this.wizardPaymentError, { position: "top-right" });
          return false;
        }

        let redirectUrl;
        if (typeof responseData === "string") {
          redirectUrl = responseData
            .replace(/^["\\"]+/, "")
            .replace(/["\\"]+$/, "")
            .trim();
        } else {
          redirectUrl = responseData;
        }

        if (!redirectUrl || !redirectUrl.startsWith("http")) {
          this.wizardPaymentError = "Payment page is not available. Please try again.";
          this.$toast.error(this.wizardPaymentError, { position: "top-right" });
          return false;
        }

        if (this.wizardDetailQuestions?.length) {
          try {
            this.setWizardLoading(true, "Uploading documents…");
            await this.saveWizardUploadedDocuments(this.wizardDetailQuestions);
          } catch {
            // continue with payment redirect
          }
        }

        sessionStorage.setItem("wizardPaymentStarted", "1");
        this.savePaymentCheckoutContext(contact, finalPrice);
        this.resetWizardBookingState({ keepVenue: true, keepReservation: true });
        this.setWizardLoading(true, "Redirecting to secure payment…");
        window.location.href = redirectUrl;
        return true;
      } catch (error) {
        const message =
          error?.response?.data?.details?.[0]?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Payment could not be started. Please try again.";
        this.wizardPaymentError = message;
        this.$toast.error(message, { position: "top-right" });
        return false;
      }
    },
    resetWizardBookingState({ keepVenue = false, keepReservation = false } = {}) {
      this.wizardStep = 1;
      this.wizardContactDetails = {};
      this.contactStepValid = false;
      this.wizardDetailsSubmitted = false;
      this.wizardCalculatedAmount = 0;
      this.wizardPaymentRate = 0;
      this.wizardStoreVenueBlockId = null;
      this.wizardPricingLoading = false;
      this.wizardPaymentError = "";
      this.confirmInProgress = false;
      this.wizardSecondPageDetails = [];
      this.wizardDetailQuestions = [];
      this.openFormsProcessing = false;

      sessionStorage.removeItem("bookingWizardContact");
      sessionStorage.removeItem("bookingWizardPaymentState");
      sessionStorage.removeItem("countDownTarget");

      if (!keepReservation) {
        this.wizardSlotReserved = false;
        sessionStorage.removeItem("venueBlockId");
      }

      if (!keepVenue) {
        this.storeaddedbookingdetails = [];
        this.storemultiBookingCartData = [];
        this.setTempBooking([]);
        this.setMultiBookingData([]);
        this.$store.commit(
          `${ENV.VUE_APP_FACILITY}_booking/temporarybookingdetails`,
          []
        );
        this.$store.commit(
          `${ENV.VUE_APP_FACILITY}_booking/multibookingdetails`,
          []
        );
        this.selectdatetime = false;
        this.singleBookingActive = true;
      }

      this.$nextTick(() => {
        this.$refs.contactStep?.resetForm?.();
      });
    },
    savePaymentCheckoutContext(contact, rate) {
      let venueBlockId = null;
      try {
        venueBlockId = JSON.parse(sessionStorage.getItem("venueBlockId"));
      } catch {
        venueBlockId = null;
      }

      const booking = this.storeaddedbookingdetails?.[0] || null;
      const context = {
        venueBlockId,
        storeVenueBlockId: this.wizardStoreVenueBlockId,
        contact,
        booking,
        rate,
        bookingType:
          sessionStorage.getItem("BookingType") || "singlebooking",
        venueName: booking?.venueName || this.selectedVenueName,
        venueId: sessionStorage.getItem("selectedVenueUniqueId"),
        startTime:
          sessionStorage.getItem("starttime") ||
          booking?.displayStartTime ||
          booking?.StartTime,
        endTime:
          sessionStorage.getItem("endtime") ||
          booking?.displayEndTime ||
          booking?.EndTime,
        isFreeBooking: sessionStorage.getItem("isFreeBooking"),
        isFulldayBooking: sessionStorage.getItem("isFulldayBooking") === "true",
      };

      sessionStorage.setItem(
        "paymentCheckoutContext",
        JSON.stringify(context)
      );
    },
    startSessionTimer() {
      this.clearSessionTimer();
      let target = Number(sessionStorage.getItem("countDownTarget"));
      if (!target || Number.isNaN(target)) {
        target = Date.now() + 10 * 60 * 1000;
        sessionStorage.setItem("countDownTarget", String(target));
      }
      this.sessionTimerInterval = setInterval(() => {
        if (this.isSessionExpired()) {
          this.sessionTimerExpired = true;
          this.clearSessionTimer();
        }
      }, 1000);
    },
    clearSessionTimer() {
      if (this.sessionTimerInterval) {
        clearInterval(this.sessionTimerInterval);
        this.sessionTimerInterval = null;
      }
    },
    isSessionExpired() {
      const target = Number(sessionStorage.getItem("countDownTarget"));
      if (!target || Number.isNaN(target)) {
        return false;
      }
      return target - Date.now() <= 0;
    },
    handleSessionExpiredConfirm() {
      this.sessionTimerExpired = false;
      sessionStorage.removeItem("countDownTarget");
      sessionStorage.removeItem("venueBlockId");
      this.resetWizardBookingState({ keepVenue: true });
      this.navigateHome();
    },
    async reserveBookingSlot({ force = false } = {}) {
      if (
        !force &&
        this.wizardSlotReserved &&
        sessionStorage.getItem("venueBlockId")
      ) {
        return true;
      }

      this.isBooked = false;

      const isMultiBooking =
        this.openmultibookingcart === true &&
        this.storemultiBookingCartData &&
        this.storemultiBookingCartData.length > 0;

      if (isMultiBooking) {
        this.$toast.error(
          "Multi-booking checkout is not supported in the wizard yet.",
          { position: "top-right" }
        );
        return false;
      }

      const isBundleVenue =
        Array.isArray(this.storeaddedbookingdetails?.[0]?.bundleVenueIds) &&
        this.storeaddedbookingdetails[0].bundleVenueIds.length > 0;

      sessionStorage.setItem(
        "BookingType",
        isBundleVenue ? "bundlebooking" : "singlebooking"
      );

      if (!this.storeaddedbookingdetails?.length) {
        this.$toast.error("Please add a session before confirming.", {
          position: "top-right",
        });
        return false;
      }

      let singleVenueId = sessionStorage.getItem("selectedVenueUniqueId");
      if (!singleVenueId) singleVenueId = this.currentUniqueVenueId;
      if (!singleVenueId) {
        this.$toast.error("No venue selected. Please select a venue first.", {
          position: "top-right",
        });
        return false;
      }
      if (Array.isArray(singleVenueId)) singleVenueId = singleVenueId[0];
      singleVenueId = String(singleVenueId).trim();
      if (singleVenueId.includes(",")) {
        singleVenueId = singleVenueId.split(",")[0].trim();
      }

      const uniqueVenueIds = [singleVenueId];
      const results = await search({
        StartDate: this.storeaddedbookingdetails[0].StartTime,
        EndDate: this.storeaddedbookingdetails[0].EndTime,
        UniqueVenueIds: uniqueVenueIds,
      });

      for (let index = 0; index < results?.length; index++) {
        if (results[index].isBooked === true) {
          this.isBooked = true;
        }
      }

      if (this.isBooked) {
        this.$toast.error(
          "This venue is not available at this date and time.",
          { position: "top-right" }
        );
        return false;
      }

      try {
        this.$store.commit(
          `${ENV.VUE_APP_FACILITY}_booking/setTemporarybookingdetails`,
          this.storeaddedbookingdetails
        );

        const selectedDetails = this.storeaddedbookingdetails.filter(
          (element) => element.venueName === this.selectedVenueName
        );

        if (!selectedDetails.length) {
          this.$toast.error("Booking session not found for this venue.", {
            position: "top-right",
          });
          return false;
        }

        const isBundle =
          selectedDetails[0].venueData?.bundleVenueIds &&
          Array.isArray(selectedDetails[0].venueData.bundleVenueIds) &&
          selectedDetails[0].venueData.bundleVenueIds.length > 0;

        let finalVenueId = sessionStorage.getItem("selectedVenueUniqueId");
        if (!finalVenueId) finalVenueId = this.currentUniqueVenueId;
        if (!finalVenueId) {
          this.$toast.error("No venue selected. Please select a venue first.", {
            position: "top-right",
          });
          return false;
        }
        if (Array.isArray(finalVenueId)) finalVenueId = finalVenueId[0];
        finalVenueId = String(finalVenueId).trim();
        if (finalVenueId.includes(",")) {
          finalVenueId = finalVenueId.split(",")[0].trim();
        }

        let res = {};
        if (isBundle) {
          res = await setBundleSearchSelect([
            {
              StartTime: selectedDetails[0].StartTime,
              EndTime: selectedDetails[0].EndTime,
              UniqueVenueId: finalVenueId,
            },
          ]);
        } else {
          const payload = selectedDetails.map((detail) => ({
            StartTime: detail.StartTime,
            EndTime: detail.EndTime,
            maxPeople: detail.maxPeople,
            UniqueVenueId: finalVenueId,
            venueName: detail.venueName,
            venueType: this.selectedvenueType,
          }));
          res = await setBookingSearchSelect(payload);
        }

        if (res.Status !== "SEARCHING") {
          this.$toast.error("Unable to reserve this session. Please try again.", {
            position: "top-right",
          });
          return false;
        }

        sessionStorage.setItem(
          "venueBlockId",
          JSON.stringify(res.venueblockids)
        );

        let originalStartTime =
          selectedDetails[0].displayStartTime || selectedDetails[0].StartTime;
        let originalEndTime =
          selectedDetails[0].displayEndTime || selectedDetails[0].EndTime;

        if (
          !selectedDetails[0].displayEndTime &&
          this.checksetdownTime &&
          this.setdownTime &&
          this.setdownTime !== "00:00" &&
          selectedDetails[0].EndTime
        ) {
          const endMoment = moment(
            selectedDetails[0].EndTime,
            "DD/MM/YYYY HH:mm"
          );
          const setdownDuration = moment.duration(this.setdownTime);
          originalEndTime = endMoment
            .clone()
            .subtract(setdownDuration)
            .format("DD/MM/YYYY HH:mm");
        }

        sessionStorage.setItem("starttime", originalStartTime);
        sessionStorage.setItem("endtime", originalEndTime);
        sessionStorage.setItem("selectedvenuename", this.selectedVenueName);
        sessionStorage.setItem("statusbar", String(this.percentagebar + 14));
        sessionStorage.setItem("description", this.description);
        sessionStorage.setItem("isFulldayBooking", this.fulldayChecked);
        sessionStorage.setItem("isHourlyBooking", this.hourlyChecked);
        sessionStorage.setItem("isFreeBooking", this.freebookingChecked);
        this.wizardSlotReserved = true;
        this.startSessionTimer();
        return true;
      } catch (error) {
        this.$toast.warning(error, { position: "top-right" });
        return false;
      }
    },
    async submitWizardBookingDetail(contact, secondPageDetails = []) {
      let venueBlockId;
      try {
        venueBlockId = JSON.parse(sessionStorage.getItem("venueBlockId"));
      } catch {
        this.$toast.error("Reservation expired. Please try again.", {
          position: "top-right",
        });
        return false;
      }

      const bookingDetailStructure = {
        venueBlockId: { venueblockid: venueBlockId },
        BookingDetails: {
          ...contact,
          BookingSource:
            sessionStorage.getItem("readOnlyOTP") === "true" ? "OTP" : "visitor",
        },
        SecondPageDetails: secondPageDetails || [],
      };

      try {
        const bookingType = sessionStorage.getItem("BookingType") || "singlebooking";
        const booking = this.storeaddedbookingdetails[0];
        const isBundle =
          bookingType === "bundlebooking" ||
          (Array.isArray(booking?.bundleVenueIds) && booking.bundleVenueIds.length > 0) ||
          (Array.isArray(booking?.venueData?.bundleVenueIds) &&
            booking.venueData.bundleVenueIds.length > 0);

        let data;
        if (isBundle) {
          const bundleId =
            sessionStorage.getItem("selectedVenueUniqueId") ||
            booking?.uniqueVenueId ||
            booking?.venueData?.id ||
            booking?.bundleVenueIds;
          data = await setBundleBookingDetail(bookingDetailStructure, bundleId);
        } else {
          data = await setBookingDetail(bookingDetailStructure);
        }

        if (data.status !== "PENDING PAYMENT") {
          this.$toast.error("Unable to submit booking details.", {
            position: "top-right",
          });
          return false;
        }

        const isFree = sessionStorage.getItem("isFreeBooking") === "true";
        const isOtp = sessionStorage.getItem("readOnlyOTP") === "true";
        const isFullday = sessionStorage.getItem("isFulldayBooking") === "true";
        const bookingamount = data.totalPrice
          ? data.totalPrice
          : data.fulldayPrice;
        let rate = bookingamount;
        if (isFree || isOtp) {
          rate = 0;
        } else if (isFullday) {
          rate = Number(data.fulldayPrice);
        }

        this.wizardPaymentRate = rate;
        this.wizardCalculatedAmount = rate;
        this.wizardStoreVenueBlockId = data.venueBlockId;
        this.wizardDetailsSubmitted = true;
        this.savePaymentCheckoutContext(contact, rate);

        const storedVenueId = sessionStorage.getItem("selectedVenueUniqueId");
        const paymentState = {
          contactDetails: contact,
          firstName: contact.FirstName,
          lastName: contact.LastName,
          rate,
          bookingamount,
          storeVenueBlockId: data.venueBlockId,
          venueName: booking?.venueName || this.selectedVenueName,
          venueId: storedVenueId
            ? String(storedVenueId).trim()
            : booking?.uniqueVenueId || "",
          pricingStartDate: booking?.StartTime,
          pricingEndDate: booking?.EndTime,
          statusbarIncrement: 30,
        };

        sessionStorage.setItem(
          "bookingWizardPaymentState",
          JSON.stringify(paymentState)
        );
        sessionStorage.setItem(
          "statusbar",
          String((this.percentagebar || 0) + 14)
        );
        return true;
      } catch (error) {
        this.$toast.warning(error, { position: "top-right" });
        return false;
      }
    },
    booksinglebooking() {
      sessionStorage.setItem("BookingType", "singlebooking");
      this.multiBookingFlowActive = false;

      if (this.multiBookingActive === false) {
        if (this.storeaddedbookingdetails.length >= 1) {
          const foundBooking = this.storeaddedbookingdetails.filter(
            (element) => element.venueName === this.selectedVenueName
          );
          if (foundBooking.length > 0) {
            this.selectdatetime = false;
          } else {
            this.selectdatetime = true;
          }
        }
      }
      this.selectdatetime = true;
      this.opensinglebookingcart = true;
      this.openmultibookingcart = false;
      this.updateSingleBookingButtonState();
    },

    async navigateHome() {
      this.$router.push({ path: `/bookingapp/${ENV.VUE_APP_FACILITY}/` });

      this.storeaddedbookingdetails = [];
      await this.setTempBooking(this.storeaddedbookingdetails);
      this.$store.commit(
        `${ENV.VUE_APP_FACILITY}_booking/temporarybookingdetails`,
        this.storeaddedbookingdetails
      );

      this.storemultiBookingCartData = [];
      await this.setMultiBookingData(this.storemultiBookingCartData);
      this.$store.commit(
        `${ENV.VUE_APP_FACILITY}_booking/multibookingdetails`,
        this.storemultiBookingCartData
      );
      this.updateSingleBookingButtonState();
      this.openmultibookingcart = false;
      this.multiBookingFlowActive = false;
    },

    updateSingleBookingButtonState() {
      const foundBooking = this.storeaddedbookingdetails.filter(
        (element) => element.venueName === this.selectedVenueName
      );
      const hasMultiBookings =
        Array.isArray(this.storemultiBookingCartData) &&
        this.storemultiBookingCartData.length > 0;

      // Check if selected venue is a bundle venue
      const isBundleVenue = this.isSelectedVenueBundle();

      if (this.multiBookingActive === false || isBundleVenue) {
        this.singleBookingActive = foundBooking.length === 0;
        this.showMultiBookingButton = false;
        return;
      }

      const canAddSingle = foundBooking.length === 0;
      this.singleBookingActive = canAddSingle;

      const multiInProgress =
        this.multiBookingFlowActive || hasMultiBookings;

      this.showMultiBookingButton = multiInProgress ? true : canAddSingle;
    },
    
    isSelectedVenueBundle() {
      // Check if the currently selected venue is a bundle venue
      if (!this.selectedVenueName || !this.venuesFromNewAPI || !Array.isArray(this.venuesFromNewAPI)) {
        return false;
      }
      
      const selectedVenueData = this.venuesFromNewAPI.find(venue => 
        (venue.venuename || venue.venueName) === this.selectedVenueName
      );
      
      if (!selectedVenueData) {
        return false;
      }
      
      // Check if it's a bundle venue (has bundlevenueids array with items)
      return selectedVenueData.bundlevenueids && 
             Array.isArray(selectedVenueData.bundlevenueids) && 
             selectedVenueData.bundlevenueids.length > 0;
    },
    
    // Extract date part from StartTime (format: "YYYY-MM-DD HH:mm:ss" or similar)
    getDateFromStartTime(startTime) {
      if (!startTime) return null;
      // Try to extract date part (first 10 characters for YYYY-MM-DD)
      if (startTime.length >= 10) {
        return startTime.substring(0, 10);
      }
      // Fallback: try to parse as Date and get date string
      try {
        const date = new Date(startTime);
        return formatLocalDateKey(date) || null;
      } catch (e) {
        return null;
      }
    },
    
    // Check if two bookings are on the same date
    isSameDate(startTime1, startTime2) {
      const date1 = this.getDateFromStartTime(startTime1);
      const date2 = this.getDateFromStartTime(startTime2);
      return date1 && date2 && date1 === date2;
    },
    
    // Check if a booking is a duplicate (same venue, date, start time, and end time)
    isDuplicateBooking(newBooking, existingBookings) {
      try {
        if (!existingBookings || existingBookings.length === 0) {
          return false;
        }
        
        const newStartTime = newBooking.StartTime || newBooking.displayStartTime;
        const newEndTime = newBooking.EndTime || newBooking.displayEndTime;
        const newVenueName = newBooking.venueName;
        
        if (!newStartTime || !newEndTime || !newVenueName) {
          return false;
        }
        
        // Normalize times for comparison (remove milliseconds if present)
        const normalizeTime = (timeStr) => {
          if (!timeStr) return '';
          // Remove milliseconds and timezone if present
          return timeStr.replace(/\.\d{3}/, '').replace(/[+-]\d{2}:\d{2}$/, '').trim();
        };
        
        const normalizedNewStart = normalizeTime(newStartTime);
        const normalizedNewEnd = normalizeTime(newEndTime);
        
        return existingBookings.some(existing => {
          const existingStartTime = existing.StartTime || existing.displayStartTime;
          const existingEndTime = existing.EndTime || existing.displayEndTime;
          const existingVenueName = existing.venueName;
          
          if (!existingStartTime || !existingEndTime || !existingVenueName) {
            return false;
          }
          
          const normalizedExistingStart = normalizeTime(existingStartTime);
          const normalizedExistingEnd = normalizeTime(existingEndTime);
          
          return existingVenueName === newVenueName &&
                 this.isSameDate(newStartTime, existingStartTime) &&
                 normalizedNewStart === normalizedExistingStart &&
                 normalizedNewEnd === normalizedExistingEnd;
        });
      } catch (error) {
        console.error('Error in isDuplicateBooking:', error);
        // Return false on error to avoid blocking valid bookings
        return false;
      }
    },
    
    // Check if two bookings are consecutive (end time of one matches start time of another)
    areConsecutive(booking1, booking2) {
      try {
        const start1 = booking1.StartTime || booking1.displayStartTime;
        const end1 = booking1.EndTime || booking1.displayEndTime;
        const start2 = booking2.StartTime || booking2.displayStartTime;
        const end2 = booking2.EndTime || booking2.displayEndTime;
        const venue1 = booking1.venueName;
        const venue2 = booking2.venueName;
        
        if (!start1 || !end1 || !start2 || !end2 || !venue1 || !venue2) {
          return false;
        }
        
        // Must be same venue and same date
        if (venue1 !== venue2 || !this.isSameDate(start1, start2)) {
          return false;
        }
        
        // Normalize times for comparison (remove milliseconds if present)
        const normalizeTime = (timeStr) => {
          if (!timeStr) return '';
          return timeStr.replace(/\.\d{3}/, '').replace(/[+-]\d{2}:\d{2}$/, '').trim();
        };
        
        const normalizedEnd1 = normalizeTime(end1);
        const normalizedStart2 = normalizeTime(start2);
        const normalizedEnd2 = normalizeTime(end2);
        const normalizedStart1 = normalizeTime(start1);
        
        // Check if end time of one matches start time of another
        return normalizedEnd1 === normalizedStart2 || normalizedEnd2 === normalizedStart1;
      } catch (error) {
        console.error('Error in areConsecutive:', error);
        return false;
      }
    },
    
    // Consolidate consecutive bookings
    consolidateConsecutiveBookings(bookings) {
      if (!bookings || bookings.length === 0) {
        return bookings;
      }
      
      // Sort bookings by venue, date, and start time
      const sorted = [...bookings].sort((a, b) => {
        const venueA = a.venueName || '';
        const venueB = b.venueName || '';
        if (venueA !== venueB) {
          return venueA.localeCompare(venueB);
        }
        
        const dateA = this.getDateFromStartTime(a.StartTime || a.displayStartTime);
        const dateB = this.getDateFromStartTime(b.StartTime || b.displayStartTime);
        if (dateA !== dateB) {
          return (dateA || '').localeCompare(dateB || '');
        }
        
        const startA = a.StartTime || a.displayStartTime || '';
        const startB = b.StartTime || b.displayStartTime || '';
        return startA.localeCompare(startB);
      });
      
      const consolidated = [];
      let currentBooking = null;
      
      for (const booking of sorted) {
        if (!currentBooking) {
          currentBooking = { ...booking };
          continue;
        }
        
        if (this.areConsecutive(currentBooking, booking)) {
          // Merge consecutive bookings
          const start1 = currentBooking.displayStartTime || currentBooking.StartTime;
          const end2 = booking.displayEndTime || booking.EndTime;
          
          // Keep the earlier start time and later end time
          if (start1 && end2) {
            // Preserve display times (original user-selected times)
            currentBooking.displayStartTime = start1;
            currentBooking.displayEndTime = end2;
            
            // Also update the adjusted times (with setdown time) for API calls
            // Calculate the adjusted end time by adding setdown time to the original end time
            if (end2 && this.setdownTime) {
              const endMoment = moment(end2, "DD/MM/YYYY HH:mm");
              const setdownDuration = moment.duration(this.setdownTime);
              const adjustedEnd = endMoment.clone().add(setdownDuration);
              currentBooking.EndTime = adjustedEnd.format("DD/MM/YYYY HH:mm");
            } else {
              currentBooking.EndTime = end2;
            }
            
            // Use the maxPeople from the booking with more people, or keep the first one
            currentBooking.maxPeople = Math.max(
              currentBooking.maxPeople || 0,
              booking.maxPeople || 0
            );
          }
        } else {
          // Not consecutive, add current booking and start new one
          consolidated.push(currentBooking);
          currentBooking = { ...booking };
        }
      }
      
      // Add the last booking
      if (currentBooking) {
        consolidated.push(currentBooking);
      }
      
      return consolidated;
    },
    
    // Main validation method for multi-booking
    validateAndProcessMultiBooking(newBooking) {
      try {
        // Check for duplicate booking
        if (this.isDuplicateBooking(newBooking, this.storemultiBookingCartData)) {
          const startTime = newBooking.StartTime || newBooking.displayStartTime;
          const endTime = newBooking.EndTime || newBooking.displayEndTime;
          const date = this.getDateFromStartTime(startTime);
          return {
            success: false,
            message: `This booking already exists: ${date} ${startTime?.substring(11, 16) || ''} to ${endTime?.substring(11, 16) || ''}`,
            updatedCart: this.storemultiBookingCartData,
            consolidated: false
          };
        }
        
        // Add new booking to cart
        const updatedCart = [...(this.storemultiBookingCartData || []), newBooking];
        
        // Consolidate consecutive bookings
        const consolidatedCart = this.consolidateConsecutiveBookings(updatedCart);
        
        // Check if consolidation occurred
        const wasConsolidated = consolidatedCart.length < updatedCart.length;
        
        if (wasConsolidated) {
          // Find the consolidated booking to show details
          const newStartTime = newBooking.StartTime || newBooking.displayStartTime;
          const consolidatedBooking = consolidatedCart.find(b => {
            const bStart = b.StartTime || b.displayStartTime;
            return this.isSameDate(bStart, newStartTime) && 
                   (bStart === newStartTime || (b.EndTime || b.displayEndTime) === (newBooking.EndTime || newBooking.displayEndTime));
          });
          
          if (consolidatedBooking) {
            const startTime = consolidatedBooking.StartTime || consolidatedBooking.displayStartTime;
            const endTime = consolidatedBooking.EndTime || consolidatedBooking.displayEndTime;
            const date = this.getDateFromStartTime(startTime);
            const startTimeStr = startTime?.substring(11, 16) || '';
            const endTimeStr = endTime?.substring(11, 16) || '';
            
            return {
              success: true,
              message: `Consecutive bookings consolidated: ${date} ${startTimeStr} to ${endTimeStr}`,
              updatedCart: consolidatedCart,
              consolidated: true
            };
          }
        }
        
        return {
          success: true,
          message: '',
          updatedCart: consolidatedCart,
          consolidated: false
        };
      } catch (error) {
        // If validation fails, log error but allow booking to proceed
        console.error('Error in validateAndProcessMultiBooking:', error);
        // Return success with original cart + new booking to avoid blocking the flow
        return {
          success: true,
          message: '',
          updatedCart: [...(this.storemultiBookingCartData || []), newBooking],
          consolidated: false
        };
      }
    },
    
    // Process multiple bookings (for daily/weekly/monthly flows)
    processMultipleBookings(newBookings, existingCart) {
      if (!newBookings || newBookings.length === 0) {
        return {
          success: true,
          updatedCart: existingCart || [],
          consolidated: false,
          duplicatesRemoved: 0
        };
      }
      
      let updatedCart = [...(existingCart || [])];
      let duplicatesRemoved = 0;
      let consolidatedCount = 0;
      const messages = [];
      
      for (const newBooking of newBookings) {
        // Check for duplicates
        if (this.isDuplicateBooking(newBooking, updatedCart)) {
          duplicatesRemoved++;
          continue;
        }
        
        // Add booking
        updatedCart.push(newBooking);
      }
      
      // Consolidate consecutive bookings
      const beforeConsolidation = updatedCart.length;
      updatedCart = this.consolidateConsecutiveBookings(updatedCart);
      const afterConsolidation = updatedCart.length;
      consolidatedCount = beforeConsolidation - afterConsolidation;
      
      // Build messages
      if (duplicatesRemoved > 0) {
        messages.push(`${duplicatesRemoved} duplicate booking(s) removed`);
      }
      if (consolidatedCount > 0) {
        messages.push(`${consolidatedCount} consecutive booking(s) consolidated`);
      }
      
      return {
        success: true,
        updatedCart: updatedCart,
        consolidated: consolidatedCount > 0,
        duplicatesRemoved: duplicatesRemoved,
        message: messages.length > 0 ? messages.join('. ') : ''
      };
    },

    Openmonthbookingcard(value) {
      if (value === 0) {
        this.openDaycard = true;
        this.openWeekcard = false;
        this.openMonthcard = false;
        this.showunavailabledates = false;
      } else if (value === 1) {
        this.openDaycard = false;
        this.openWeekcard = true;
        this.openMonthcard = false;
        this.showunavailabledates = false;
      } else if (value === 2) {
        this.openDaycard = false;
        this.openWeekcard = false;
        this.openMonthcard = true;
        this.CountDays = this.Counts[0];
        this.SelectedWeekDay = this.Weekdays[0];
        this.showunavailabledates = false;
      }
    },

    checkedmonthlyday(value) {
      if (value == 0) {
        this.checkedDay = true;
        this.checkedThe = false;
      } else {
        this.checkedDay = false;
        this.checkedThe = true;
      }
    },

    bookmultibooking() {
      sessionStorage.setItem("BookingType", "multibooking");
      this.multiBookingFlowActive = true;
      this.selectdatetime = true;
      this.openmultibookingbox = false;
      this.openmultibookingcart = true;
      this.opensinglebookingcart = false;
      this.checkedThe = false;
      this.openWeekcard = false;
      this.openMonthcard = false;
      this.openDaycard = true;
      this.showMultiBookingButton = true;
      this.updateSingleBookingButtonState();
    },

    async deleteSelectedVenue(index) {
      let newlist = [];
      for (let i = 0; i < this.storeaddedbookingdetails?.length; i++) {
        if (index != i) {
          newlist.push(this.storeaddedbookingdetails[i]);
        } else {
          this.DifferenceHours(
            this.storeaddedbookingdetails[i].StartTime,
            this.storeaddedbookingdetails[i].EndTime,
            "delete",
            this.selectedVenueName
          );
        }
      }

      await this.setTempBooking(newlist);
      this.$store.commit(
        `${ENV.VUE_APP_FACILITY}_booking/temporarybookingdetails`,
        newlist
      );
      this.storeaddedbookingdetails = getTemporarybookingDetails();
      if (this.storeaddedbookingdetails === undefined) {
        this.storeaddedbookingdetails = [];
      }
      this.updateSingleBookingButtonState();
    },

    async deleteSelectedMultiBookingVenue(details, index) {
      let newlist = [];
      for (let i = 0; i < this.storemultiBookingCartData?.length; i++) {
        if (index != i) {
          newlist.push(this.storemultiBookingCartData[i]);
        } else {
          this.DifferenceHours(
            this.storemultiBookingCartData[i].StartTime,
            this.storemultiBookingCartData[i].EndTime,
            "delete",
            this.selectedVenueName
          );
        }
      }
      await this.setMultiBookingData(newlist);
      this.$store.commit(
        `${ENV.VUE_APP_FACILITY}_booking/multibookingdetails`,
        newlist
      );
      this.storemultiBookingCartData = getMultibookingDetails();
      if (this.storemultiBookingCartData === undefined) {
        this.storemultiBookingCartData = [];
      }
      if (this.storemultiBookingCartData.length === 0) {
        this.openmultibookingcart = false;
        this.multiBookingFlowActive = false;
      }
      this.updateSingleBookingButtonState();
    },

    getValidHours(start, end) {
      let validateHours = [];
      for (let index = start; index <= end; index++) {
        validateHours.push(index);
      }
      return validateHours;
    },

    updateSearchDate() {
      let currentEndHour = this.endTime.getHours().toString();
      let currentMinutes = this.endTime.getMinutes().toString();
      let checkSelectedDateName = getBookingWeekdayName(this.date);
      if (currentMinutes == "0") {
        currentMinutes = currentMinutes.toString() + "0";
      }
      let currentEndTime = currentEndHour + currentMinutes;
      let workingdayEndTime =
        this.workingHoursConfig.workingDays.endTime.toString();
      let saturdayEndTime =
        this.workingHoursConfig.saturdayWorkingHours.endTime.toString();
      let sundayEndTime =
        this.workingHoursConfig.sundayWorkingHours.endTime.toString();
      if (checkSelectedDateName == "Saturday") {
        if (parseInt(currentEndTime) > parseInt(saturdayEndTime)) {
          this.errorMessageTime =
            saturdayEndTime.toString().slice(0, -2) +
            ":" +
            saturdayEndTime.toString().slice(2);
          this.invalidBooking = true;
        } else {
          this.invalidBooking = false;
        }
      } else if (checkSelectedDateName == "Sunday") {
        if (parseInt(currentEndTime) > parseInt(sundayEndTime)) {
          this.errorMessageTime =
            sundayEndTime.toString().slice(0, -2) +
            ":" +
            sundayEndTime.toString().slice(2);
          this.invalidBooking = true;
        } else {
          this.invalidBooking = false;
        }
      } else {
        if (parseInt(currentEndTime) > parseInt(workingdayEndTime)) {
          this.errorMessageTime =
            workingdayEndTime.toString().slice(0, -2) +
            ":" +
            workingdayEndTime.toString().slice(2);
          this.invalidBooking = true;
        } else {
          this.invalidBooking = false;
        }
      }
    },
    async AddMultBooking() {
      this.loading[3] = true;
      this.showunavailabledates = false;
      this.unavailableDate = [];
      sessionStorage.setItem(this.selectedVenueName, 0);
      let minPeople = "";
      let maxPeople = "";

      for (let index = 0; index < this.bookingVenuesId.length; index++) {
        const venues = this.bookingVenuesId[index];
        if (this.selectedVenueName === venues.venueName) {
          minPeople = venues.minPeople;
          maxPeople = venues.maxPeople;
        }
      }
      // this.openmultibookingcart = true
      //SECTION:: DAILY
      if (this.openDaycard == true) {
        if (this.maxPeopleDaily === "") {
          this.$toast.error("Minimum 1 person required for booking", {
            position: "top-right",
          });
          this.loading[3] = false;
          return;
        }
        if (this.maxPeopleDaily > maxPeople) {
          this.$toast.error("Maximum amount of people allowed exceeded.", {
            position: "top-right",
          });
          this.loading[3] = false;
          return;
        }
        if (this.maxPeopleDaily < minPeople) {
          this.$toast.error(
            `Minimum ${minPeople} person required for this booking`,
            {
              position: "top-right",
            }
          );
          this.loading[3] = false;
          return;
        }
        if (this.startDateDaily === "" || this.endDateDaily === "") {
          this.loading[3] = false;
          this.$toast.error("Start date and end date is required", {
            position: "top-right",
          });
          return;
        }

        let currentStartTime, currentEndTime, customEndTime;
        
        if (this.fulldayChecked) {
          // For full day bookings, use default times (7:00 AM - 9:00 PM)
          currentStartTime = "07:00";
          currentEndTime = "21:00";
          customEndTime = "2100";
        } else {
          // For hourly bookings, use selected times
          currentStartTime =
            this.formatDateNumber(this.startTimeDaily.getHours()) +
            ":" +
            this.formatDateNumber(this.startTimeDaily.getMinutes());
          currentEndTime =
            this.formatDateNumber(this.endTimeDaily.getHours()) +
            ":" +
            this.formatDateNumber(this.endTimeDaily.getMinutes());
          customEndTime =
            this.formatDateNumber(this.endTimeDaily.getHours()) +
            this.formatDateNumber(this.endTimeDaily.getMinutes());
        }
        const datesBetween = this.getDatesBetween(
          this.startDateDaily,
          this.endDateDaily
        );

        const bookingStartDateTime = new Date(
          `${this.startDateDaily}T${currentStartTime}:00`
        );
        const bookingEndDateTime = new Date(
          `${this.endDateDaily}T${currentEndTime}:00`
        );

        // Only validate start/end time if not a full day booking
        if (!this.fulldayChecked && bookingStartDateTime > bookingEndDateTime) {
          this.loading[3] = false;
          this.$toast.error("Start time cannot be after end time", {
            position: "top-right",
          });
          return;
        }

        let storenewData = [];
        for (let index = 0; index < datesBetween.length; index++) {
          const element = datesBetween[index];
          let date1 = new Date(element);
          for (
            let index = 0;
            index < this.storemultiBookingCartData?.length;
            index++
          ) {
            const element = this.storemultiBookingCartData[index];
            let date2 = new Date(element.StartTime);
            if (date1.getTime() === date2.getTime()) {
              this.validationError = true;
              this.loading[3] = false;
              return;
            }
          }

          if (this.fulldayChecked == true) {
            storenewData.push({
              venueName: this.selectedVenueName,
              StartTime: element,
              startHours: this.facility === "fchq" ? "08:00" : "07:00",
              endHours: this.facility === "fchq" ? "19:00" : "22:00",
              maxPeople: this.maxPeopleDaily,
              venueType: this.selectedvenueType,
            });
          } else {
            storenewData.push({
              venueName: this.selectedVenueName,
              StartTime: element,
              startHours: currentStartTime,
              endHours: currentEndTime,
              maxPeople: this.maxPeopleDaily,
              venueType: this.selectedvenueType,
            });
          }
        }

        let newArray = [];
        for (let index = 0; index < storenewData.length; index++) {
          const element = storenewData[index];
          const dateObject = new Date(element.StartTime);
          const dayOfWeek = dateObject.getDay();
          const dayName = this.Weekdays[dayOfWeek];

          if (dayName == "Saturday") {
            if (
              parseInt(customEndTime) >
              parseInt(this.WorkingHours.saturdayWorkingHours.endTime)
            ) {
              newArray = storenewData.filter((item) => item !== element);
              this.showunavailabledates = true;
              this.unavailableDate.push(element.StartTime);
              this.loading[3] = false;
            }
            storenewData[index].startHours =
              this.facility === "fchq" ? "08:00" : "07:00";
            storenewData[index].endHours =
              this.facility === "fchq" ? "19:00" : "22:00";
          }
          if (dayName == "Sunday") {
            if (
              parseInt(customEndTime) >
              parseInt(this.WorkingHours.sundayWorkingHours.endTime)
            ) {
              newArray = storenewData.filter((item) => item !== element);
              storenewData = newArray;
              this.showunavailabledates = true;
              this.unavailableDate.push(element.StartTime);
              this.loading[3] = false;
            }
            storenewData[index].startHours =
              this.facility === "fchq" ? "08:00" : "07:00";
            storenewData[index].endHours =
              this.facility === "fchq" ? "19:00" : "22:00";
          }
        }
        let updateDate = [];
        for (let index = 0; index < storenewData.length; index++) {
          const element = storenewData[index];
          updateDate.push({
            StartDate: element.StartTime + " " + element.startHours,
            EndDate: element.StartTime + " " + element.endHours,
          });
        }
        // Use currentUniqueVenueId for API call
        const results = await multiBookingsearch({
          Date: updateDate,
          UniqueVenueIds: this.currentUniqueVenueId ? [this.currentUniqueVenueId] : [],
        });
        if (results?.length < 1 || results === null) {
          this.$toast.error("Invalid date or time..", {
            position: "top-right",
          });
          this.loading[3] = false;
        }
        // this.unavailableDate = []
        let updateList = [];
        for (let index = 0; index < results.length; index++) {
          const element = results[index];
          if (element.IsBooked === false) {
            // Get original user-selected times (before setdown time) from storenewData
            const originalStartTime = storenewData[index].StartTime + " " + storenewData[index].startHours;
            const originalEndTime = storenewData[index].StartTime + " " + storenewData[index].endHours;
            
            updateList.push({
              venueName: storenewData[index].venueName,
              maxPeople: storenewData[index].maxPeople,
              VenueId: element.VenueId,
              IsBusiness: false,
              StartTime: element.StartDate, // API response (may have setdown time)
              EndTime: element.EndDate, // API response (may have setdown time)
              displayStartTime: originalStartTime, // Original user-selected time
              displayEndTime: originalEndTime, // Original user-selected time
              venueType: this.selectedvenueType,
            });
          } else {
            // Immediate error for bundle booking when any venue is booked
            this.$toast.error(
              "One or more venues in this bundle are already booked at the selected date and time. Please choose a different time slot.",
              { position: "top-center", timeout: 5000 }
            );
            this.loading[3] = false;
            return;
          }
        }

        if (this.storemultiBookingCartData?.length > 1) {
          const startTimeArray = this.storemultiBookingCartData.map(
            (multiBookingEle) => {
              return multiBookingEle.StartTime;
            }
          );
          const nonMatching = updateList.filter((date1) => {
            return !startTimeArray.includes(date1.StartTime);
          });
          for (let index = 0; index < nonMatching.length; index++) {
            const element = nonMatching[index];
            this.storemultiBookingCartData.push(element);
          }
        } else {
          this.storemultiBookingCartData = updateList;
        }

        for (
          let index = 0;
          index < this.storemultiBookingCartData.length;
          index++
        ) {
          const element = this.storemultiBookingCartData[index];
          if (this.fulldayChecked == false) {
            this.DifferenceHours(
              element.StartTime,
              element.EndTime,
              "add",
              this.selectedVenueName
            );
          }
        }

        await this.setMultiBookingData(this.storemultiBookingCartData);
        this.$store.commit(
          `${ENV.VUE_APP_FACILITY}_booking/setMultibookingdetails`,
          this.storemultiBookingCartData
        );
        this.loading[3] = false;
        (this.startDateDaily = ""), (this.endDateDaily = "");
        this.maxPeopleDaily = "";
        if (this.showunavailabledates == true) {
          this.openmultibookingbox = true;
        } else {
          this.openmultibookingbox = false;
        }
      }
      //SECTION:: weekly
      else if (this.openWeekcard == true) {
        if (this.maxPeopleWeekly === "") {
          this.loading[3] = false;
          this.$toast.error("Minimum 1 person required for booking", {
            position: "top-right",
          });
          return;
        }
        if (this.maxPeopleWeekly > maxPeople) {
          this.$toast.error("Maximum amount of people allowed exceeded.", {
            position: "top-right",
          });
          this.loading[3] = false;
          return;
        }
        if (this.maxPeopleWeekly < minPeople) {
          this.$toast.error(
            `Minimum ${minPeople} person required for this booking`,
            {
              position: "top-right",
            }
          );
          this.loading[3] = false;
          return;
        }
        if (this.startDateWeekly === "" || this.endDateWeekly === "") {
          this.loading[3] = false;
          this.$toast.error("Start date and end date is required", {
            position: "top-right",
          });
          return;
        }
        let currentStartTime, currentEndTime;
        
        if (this.fulldayChecked) {
          // For full day bookings, use default times (7:00 AM - 9:00 PM)
          currentStartTime = "07:00";
          currentEndTime = "21:00";
        } else {
          // For hourly bookings, use selected times
          currentStartTime =
            this.formatDateNumber(this.startTimeWeekly.getHours()) +
            ":" +
            this.formatDateNumber(this.startTimeWeekly.getMinutes());
          currentEndTime =
            this.formatDateNumber(this.endTimeWeekly.getHours()) +
            ":" +
            this.formatDateNumber(this.endTimeWeekly.getMinutes());
        }
        let customEndTime;
        if (this.fulldayChecked) {
          customEndTime = "2100";
        } else {
          customEndTime =
            this.formatDateNumber(this.endTimeWeekly.getHours()) +
            this.formatDateNumber(this.endTimeWeekly.getMinutes());
        }
        const datesOnSelectedDay = this.getDatesOnSelectedDays(
          this.startDateWeekly,
          this.endDateWeekly,
          this.selectDays
        );

        const bookingStartDateTime = new Date(
          `${this.startDateWeekly}T${currentStartTime}:00`
        );
        const bookingEndDateTime = new Date(
          `${this.endDateWeekly}T${currentEndTime}:00`
        );

        // Only validate start/end time if not a full day booking
        if (!this.fulldayChecked && bookingStartDateTime > bookingEndDateTime) {
          this.loading[3] = false;
          this.$toast.error("Start time cannot be after end time", {
            position: "top-right",
          });
          return;
        }

        let storenewData = [];
        for (let index = 0; index < datesOnSelectedDay.length; index++) {
          const element = datesOnSelectedDay[index];
          let date1 = new Date(element);
          for (
            let index = 0;
            index < this.storemultiBookingCartData?.length;
            index++
          ) {
            const element = this.storemultiBookingCartData[index];
            let date2 = new Date(element.StartTime);
            if (date1.getTime() === date2.getTime()) {
              this.validationError = true;
              this.loading[3] = false;
              return;
            }
          }

          if (this.fulldayChecked == true) {
            storenewData.push({
              venueName: this.selectedVenueName,
              StartTime: element,
              startHours: this.facility === "fchq" ? "08:00" : "07:00",
              endHours: this.facility === "fchq" ? "19:00" : "22:00",
              maxPeople: this.maxPeopleWeekly,
              venueType: this.selectedvenueType,
            });
          } else {
            storenewData.push({
              venueName: this.selectedVenueName,
              StartTime: element,
              startHours: currentStartTime,
              endHours: currentEndTime,
              maxPeople: this.maxPeopleWeekly,
              venueType: this.selectedvenueType,
            });
          }
        }

        let newArray = [];
        for (let index = 0; index < storenewData.length; index++) {
          const element = storenewData[index];
          const dateObject = new Date(element.StartTime);
          const dayOfWeek = dateObject.getDay();
          const dayName = this.Weekdays[dayOfWeek];

          if (dayName === "Saturday") {
            if (
              parseInt(customEndTime) >
              parseInt(this.WorkingHours.saturdayWorkingHours.endTime)
            ) {
              newArray = storenewData.filter((item) => item !== element);
              this.showunavailabledates = true;
              this.unavailableDate.push(element.StartTime);
            }
            storenewData[index].startHours =
              this.facility === "fchq" ? "08:00" : "07:00";
            storenewData[index].endHours =
              this.facility === "fchq" ? "19:00" : "22:00";
          }
          if (dayName === "Sunday") {
            if (
              parseInt(customEndTime) >
              parseInt(this.WorkingHours.sundayWorkingHours.endTime)
            ) {
              newArray = storenewData.filter((item) => item !== element);
              storenewData = newArray;
              this.showunavailabledates = true;
              this.unavailableDate.push(element.StartTime);
            }
            storenewData[index].startHours =
              this.facility === "fchq" ? "08:00" : "07:00";
            storenewData[index].endHours =
              this.facility === "fchq" ? "19:00" : "22:00";
          }
        }

        let updateDate = [];
        for (let index = 0; index < storenewData.length; index++) {
          const element = storenewData[index];
          updateDate.push({
            StartDate: element.StartTime + " " + element.startHours,
            EndDate: element.StartTime + " " + element.endHours,
          });
        }
        // Use currentUniqueVenueId for API call
        const results = await multiBookingsearch({
          Date: updateDate,
          UniqueVenueIds: this.currentUniqueVenueId ? [this.currentUniqueVenueId] : [],
        });
        if (results?.length < 1 || results === null) {
          this.$toast.error("Invalid date or time..", {
            position: "top-right",
          });
          this.loading[3] = false;
        }
        // this.unavailableDate = []
        let updateList = [];
        for (let index = 0; index < results.length; index++) {
          const element = results[index];
          if (element.IsBooked === false) {
            // Get original user-selected times (before setdown time) from storenewData
            const originalStartTime = storenewData[index].StartTime + " " + storenewData[index].startHours;
            const originalEndTime = storenewData[index].StartTime + " " + storenewData[index].endHours;
            
            updateList.push({
              venueName: storenewData[index].venueName,
              maxPeople: storenewData[index].maxPeople,
              VenueId: element.VenueId,
              IsBusiness: false,
              StartTime: element.StartDate, // API response (may have setdown time)
              EndTime: element.EndDate, // API response (may have setdown time)
              displayStartTime: originalStartTime, // Original user-selected time
              displayEndTime: originalEndTime, // Original user-selected time
              venueType: this.selectedvenueType,
            });
          } else {
            // Immediate error for bundle booking when any venue is booked
            this.$toast.error(
              "One or more venues in this bundle are already booked at the selected date and time. Please choose a different time slot.",
              { position: "top-center", timeout: 5000 }
            );
            this.loading[3] = false;
            return;
          }
        }

        if (this.storemultiBookingCartData?.length > 1) {
          const startTimeArray = this.storemultiBookingCartData.map(
            (multiBookingEle) => {
              return multiBookingEle.StartTime;
            }
          );
          const nonMatching = updateList.filter((date1) => {
            return !startTimeArray.includes(date1.StartTime);
          });
          for (let index = 0; index < nonMatching.length; index++) {
            const element = nonMatching[index];
            this.storemultiBookingCartData.push(element);
          }
        } else {
          this.storemultiBookingCartData = updateList;
        }
        this.loading[3] = false;
        // this.openmultibookingcart = true

        for (
          let index = 0;
          index < this.storemultiBookingCartData.length;
          index++
        ) {
          const element = this.storemultiBookingCartData[index];
          if (this.fulldayChecked == false) {
            this.DifferenceHours(
              element.StartTime,
              element.EndTime,
              "add",
              this.selectedVenueName
            );
          }
        }

        await this.setMultiBookingData(this.storemultiBookingCartData);
        this.$store.commit(
          `${ENV.VUE_APP_FACILITY}_booking/setMultibookingdetails`,
          this.storemultiBookingCartData
        );
        this.selectDays = [];
        (this.startDateWeekly = ""), (this.endDateWeekly = "");
        this.maxPeopleWeekly = "";
        if (this.showunavailabledates == true) {
          this.openmultibookingbox = true;
        } else {
          this.openmultibookingbox = false;
        }
      } else {
        //SECTION:: MONTHLY
        if (this.maxPeopleMonthly === "") {
          this.loading[3] = false;
          this.$toast.error("Minimum 1 person required for booking", {
            position: "top-right",
          });
          return;
        }

        if (this.maxPeopleMonthly > maxPeople) {
          this.$toast.error("Maximum amount of people allowed exceeded.", {
            position: "top-right",
          });
          this.loading[3] = false;
          return;
        }
        if (this.maxPeopleMonthly < minPeople) {
          this.$toast.error(
            `Minimum ${minPeople} person required for this booking`,
            {
              position: "top-right",
            }
          );
          this.loading[3] = false;
          return;
        }

        if (this.startDateMonthly === "" || this.endDateMonthly === "") {
          this.loading[3] = false;
          this.$toast.error("Start date and end date is required", {
            position: "top-right",
          });
          return;
        }

        if (this.checkedDay == true) {
          let currentStartTime, currentEndTime;
          
          if (this.fulldayChecked) {
            // For full day bookings, use default times (7:00 AM - 9:00 PM)
            currentStartTime = "07:00";
            currentEndTime = "21:00";
          } else {
            // For hourly bookings, use selected times
            currentStartTime =
              this.formatDateNumber(this.startTimeMonthly.getHours()) +
              ":" +
              this.formatDateNumber(this.startTimeMonthly.getMinutes());
            currentEndTime =
              this.formatDateNumber(this.endTimeMonthly.getHours()) +
              ":" +
              this.formatDateNumber(this.endTimeMonthly.getMinutes());
          }
          let monthlyDate = this.filterDatesByUniqueDay(
            this.startDateMonthly,
            this.endDateMonthly,
            this.MonthlyDays
          );
          let customEndTime =
            this.formatDateNumber(this.endTimeMonthly.getHours()) +
            this.formatDateNumber(this.endTimeMonthly.getMinutes());

          const bookingStartDateTime = new Date(
            `${this.startDateMonthly}T${currentStartTime}:00`
          );
          const bookingEndDateTime = new Date(
            `${this.endDateMonthly}T${currentEndTime}:00`
          );

          if (!this.fulldayChecked && bookingStartDateTime > bookingEndDateTime) {
            this.loading[3] = false;
            this.$toast.error("Start time cannot be after end time", {
              position: "top-right",
            });
            return;
          }

          let storenewData = [];
          for (let index = 0; index < monthlyDate?.length; index++) {
            const element = monthlyDate[index];
            let date1 = new Date(element);
            for (
              let index = 0;
              index < this.storemultiBookingCartData?.length;
              index++
            ) {
              const element = this.storemultiBookingCartData[index];
              let date2 = new Date(element.StartTime);
              if (date1.getTime() === date2.getTime()) {
                this.validationError = true;
                this.loading[3] = false;
                return;
              }
            }
            if (this.fulldayChecked == true) {
              storenewData.push({
                venueName: this.selectedVenueName,
                StartTime: element,
                startHours: "06:00",
                endHours: "23:59",
                maxPeople: this.maxPeopleMonthly,
                venueType: this.selectedvenueType,
              });
            } else {
              storenewData.push({
                venueName: this.selectedVenueName,
                StartTime: element,
                startHours: currentStartTime,
                endHours: currentEndTime,
                maxPeople: this.maxPeopleMonthly,
                venueType: this.selectedvenueType,
              });
            }
          }

          let newArray = [];
          for (let index = 0; index < storenewData.length; index++) {
            const element = storenewData[index];
            const dateObject = new Date(element.StartTime);
            const dayOfWeek = dateObject.getDay();
            const dayName = this.Weekdays[dayOfWeek];
            if (dayName == "Saturday") {
              if (
                parseInt(customEndTime) >
                parseInt(this.WorkingHours.saturdayWorkingHours.endTime)
              ) {
                newArray = storenewData.filter((item) => item !== element);
                storenewData = newArray;
                this.showunavailabledates = true;
                this.unavailableDate.push(element.StartTime);
              }
              storenewData[index].startHours =
                this.facility === "fchq" ? "08:00" : "07:00";
              storenewData[index].endHours =
                this.facility === "fchq" ? "19:00" : "22:00";
            }
            if (dayName == "Sunday") {
              if (
                parseInt(customEndTime) >
                parseInt(this.WorkingHours.sundayWorkingHours.endTime)
              ) {
                newArray = storenewData.filter((item) => item !== element);
                storenewData = newArray;
                this.showunavailabledates = true;
                this.unavailableDate.push(element.StartTime);
              }
              storenewData[index].startHours =
                this.facility === "fchq" ? "08:00" : "07:00";
              storenewData[index].endHours =
                this.facility === "fchq" ? "19:00" : "22:00";
            }
          }

          let updateDate = [];
          for (let index = 0; index < storenewData.length; index++) {
            const element = storenewData[index];
            updateDate.push({
              StartDate: element.StartTime + " " + element.startHours,
              EndDate: element.StartTime + " " + element.endHours,
            });
          }
          
          // Use currentUniqueVenueId for API call
          const results = await multiBookingsearch({
            Date: updateDate,
            UniformVenueIds: this.currentUniqueVenueId ? [this.currentUniqueVenueId] : [],
          });
          if (results?.length < 1 || results === null) {
            this.$toast.error("Invalid date or time..", {
              position: "top-right",
            });
            this.loading[3] = false;
          }
          // this.unavailableDate = []
          let updateList = [];
          for (let index = 0; index < results.length; index++) {
            const element = results[index];
            if (element.IsBooked === false) {
              updateList.push({
                venueName: storenewData[index].venueName,
                maxPeople: storenewData[index].maxPeople,
                VenueId: element.VenueId,
                IsBusiness: false,
                StartTime: element.StartDate,
                EndTime: element.EndDate,
                venueType: this.selectedvenueType,
              });
            } else {
              this.showunavailabledates = true;
              this.unavailableDate.push(element.StartDate);
            }
          }

          if (this.storemultiBookingCartData?.length > 1) {
            const startTimeArray = this.storemultiBookingCartData.map(
              (multiBookingEle) => {
                return multiBookingEle.StartTime;
              }
            );
            const nonMatching = updateList.filter((date1) => {
              return !startTimeArray.includes(date1.StartTime);
            });
            for (let index = 0; index < nonMatching.length; index++) {
              const element = nonMatching[index];
              this.storemultiBookingCartData.push(element);
            }
          } else {
            this.storemultiBookingCartData = updateList;
          }
          this.loading[3] = false;
          // this.openmultibookingcart = true
          for (
            let index = 0;
            index < this.storemultiBookingCartData.length;
            index++
          ) {
            const element = this.storemultiBookingCartData[index];
            if (this.fulldayChecked == false) {
              this.DifferenceHours(
                element.StartTime,
                element.EndTime,
                "add",
                this.selectedVenueName
              );
            }
          }

          await this.setMultiBookingData(this.storemultiBookingCartData);
          this.$store.commit(
            `${ENV.VUE_APP_FACILITY}_booking/setMultibookingdetails`,
            this.storemultiBookingCartData
          );
          (this.startDateMonthly = ""), (this.endDateMonthly = "");
          this.maxPeopleMonthly = "";
          if (this.showunavailabledates == true) {
            this.openmultibookingbox = true;
          } else {
            this.openmultibookingbox = false;
          }
        } else if (this.checkedThe == true) {
          let currentStartTime =
            this.formatDateNumber(this.startTimeMonthly.getHours()) +
            ":" +
            this.formatDateNumber(this.startTimeMonthly.getMinutes());
          let currentEndTime =
            this.formatDateNumber(this.endTimeMonthly.getHours()) +
            ":" +
            this.formatDateNumber(this.endTimeMonthly.getMinutes());
          const filteredDates2 = this.filterDatesByDayOfWeekOccurrence(
            this.startDateMonthly,
            this.endDateMonthly,
            this.CountDays,
            this.SelectedWeekDay
          );
          let customEndTime =
            this.formatDateNumber(this.endTimeMonthly.getHours()) +
            this.formatDateNumber(this.endTimeMonthly.getMinutes());
          let storenewData = [];
          for (let index = 0; index < filteredDates2.length; index++) {
            const element = filteredDates2[index];
            let date1 = new Date(element);
            for (
              let index = 0;
              index < this.storemultiBookingCartData?.length;
              index++
            ) {
              const element = this.storemultiBookingCartData[index];
              let date2 = new Date(element.StartTime);
              if (date1.getTime() === date2.getTime()) {
                this.validationError = true;
                this.loading[3] = false;
                return;
              }
            }
            if (this.fulldayChecked == true) {
              storenewData.push({
                venueName: this.selectedVenueName,
                StartTime: element,
                startHours: this.facility === "fchq" ? "08:00" : "07:00",
                endHours: this.facility === "fchq" ? "19:00" : "22:00",
                maxPeople: this.maxPeopleMonthly,
                venueType: this.selectedvenueType,
              });
            } else {
              storenewData.push({
                venueName: this.selectedVenueName,
                StartTime: element,
                startHours: currentStartTime,
                endHours: currentEndTime,
                maxPeople: this.maxPeopleMonthly,
                venueType: this.selectedvenueType,
              });
            }
          }

          let newArray = [];
          for (let index = 0; index < storenewData.length; index++) {
            const element = storenewData[index];
            const dateObject = new Date(element.StartTime);
            const dayOfWeek = dateObject.getDay();
            const dayName = this.Weekdays[dayOfWeek];

            if (dayName == "Saturday") {
              if (
                parseInt(customEndTime) >
                parseInt(this.WorkingHours.saturdayWorkingHours.endTime)
              ) {
                newArray = storenewData.filter((item) => item !== element);
                storenewData = newArray;
                this.showunavailabledates = true;
                this.unavailableDate.push(element.StartTime);
              }
              storenewData[index].startHours =
                this.facility === "fchq" ? "08:00" : "07:00";
              storenewData[index].endHours =
                this.facility === "fchq" ? "19:00" : "22:00";
            }
            if (dayName == "Sunday") {
              if (
                parseInt(customEndTime) >
                parseInt(this.WorkingHours.sundayWorkingHours.endTime)
              ) {
                newArray = storenewData.filter((item) => item !== element);
                storenewData = newArray;
                this.showunavailabledates = true;
                this.unavailableDate.push(element.StartTime);
              }
              storenewData[index].startHours =
                this.facility === "fchq" ? "08:00" : "07:00";
              storenewData[index].endHours =
                this.facility === "fchq" ? "19:00" : "22:00";
            }
          }
          
          // Use currentUniqueVenueId for API call
          let updateDate = [];
          for (let index = 0; index < storenewData.length; index++) {
            const element = storenewData[index];
            updateDate.push({
              StartDate: element.StartTime + " " + element.startHours,
              EndDate: element.StartTime + " " + element.endHours,
            });
          }
          const results = await multiBookingsearch({
            Date: updateDate,
            UniformVenueIds: this.currentUniqueVenueId ? [this.currentUniqueVenueId] : [],
          });
          if (results?.length < 1 || results === null) {
            this.$toast.error("Invalid date or time..", {
              position: "top-right",
            });
            this.loading[3] = false;
          }
          // this.unavailableDate = []
          let updateList = [];
          for (let index = 0; index < results.length; index++) {
            const element = results[index];
            if (element.IsBooked === false) {
              updateList.push({
                venueName: storenewData[index].venueName,
                maxPeople: storenewData[index].maxPeople,
                VenueId: element.VenueId,
                IsBusiness: false,
                StartTime: element.StartDate,
                EndTime: element.EndDate,
                venueType: this.selectedvenueType,
              });
            } else {
              this.showunavailabledates = true;
              this.unavailableDate.push(element.StartDate);
            }
          }

          if (this.storemultiBookingCartData?.length > 1) {
            const startTimeArray = this.storemultiBookingCartData.map(
              (multiBookingEle) => {
                return multiBookingEle.StartTime;
              }
            );
            const nonMatching = updateList.filter((date1) => {
              return !startTimeArray.includes(date1.StartTime);
            });
            for (let index = 0; index < nonMatching.length; index++) {
              const element = nonMatching[index];
              this.storemultiBookingCartData.push(element);
            }
          } else {
            this.storemultiBookingCartData = updateList;
          }
          this.loading[3] = false;
          // this.openmultibookingcart = true
          for (
            let index = 0;
            index < this.storemultiBookingCartData.length;
            index++
          ) {
            const element = this.storemultiBookingCartData[index];
            if (this.fulldayChecked == false) {
              this.DifferenceHours(
                element.StartTime,
                element.EndTime,
                "add",
                this.selectedVenueName
              );
            }
          }

          await this.setMultiBookingData(this.storemultiBookingCartData);
          this.$store.commit(
            `${ENV.VUE_APP_FACILITY}_booking/setMultibookingdetails`,
            this.storemultiBookingCartData
          );
          (this.startDateMonthly = ""), (this.endDateMonthly = "");
          this.maxPeopleMonthly = "";
          if (this.showunavailabledates == true) {
            this.openmultibookingbox = true;
          } else {
            this.openmultibookingbox = false;
          }
        }
      }
    },

    getDatesBetween(startDate, endDate) {
      let currentDate = new Date(startDate);
      let currentEndDate = new Date(endDate);
      const dates = [];
      while (currentDate <= currentEndDate) {
        const year = currentDate.getFullYear();
        const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
        const day = currentDate.getDate().toString().padStart(2, "0");
        const formattedDate = `${day}/${month}/${year}`;
        dates.push(formattedDate);
        currentDate.setDate(currentDate.getDate() + 1);
      }
      return dates;
    },

    getDatesOnSelectedDays(startDate, endDate, selectedDays) {
      const days = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      let currentDate = new Date(startDate);
      let currentEndDate = new Date(endDate);
      const dates = [];
      while (currentDate <= currentEndDate) {
        const currentDay = days[currentDate.getDay()];
        if (selectedDays.includes(currentDay)) {
          const year = currentDate.getFullYear();
          const month = (currentDate.getMonth() + 1)
            .toString()
            .padStart(2, "0");
          const day = currentDate.getDate().toString().padStart(2, "0");
          const formattedDate = `${day}/${month}/${year}`;
          dates.push(formattedDate);
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      return dates;
    },

    filterDatesByUniqueDay(startDate, endDate, uniqueDay) {
      const currentDate = new Date();

      let newStartDate = new Date(startDate);
      const currentEndDate = new Date(endDate);
      const filteredDates = [];

      if (newStartDate >= currentEndDate) {
        return filteredDates;
      }

      let currentMonth = newStartDate.getMonth();
      let currentYear = newStartDate.getFullYear();
      let currentDay = uniqueDay;

      if (currentDate.getDate() > uniqueDay) {
        currentMonth += 1;
        if (currentMonth > 11) {
          currentMonth = 0;
          currentYear += 1;
        }
      }

      while (
        currentYear < currentEndDate.getFullYear() ||
        (currentYear === currentEndDate.getFullYear() &&
          currentMonth <= currentEndDate.getMonth())
      ) {
        const formattedMonth = String(currentMonth + 1).padStart(2, "0");
        const formattedDay = String(currentDay).padStart(2, "0");

        const formattedDate = `${formattedDay}/${formattedMonth}/${currentYear}`;
        filteredDates.push(formattedDate);

        currentMonth += 1;
        if (currentMonth > 11) {
          currentMonth = 0;
          currentYear += 1;
        }
      }
      return filteredDates;
    },

    filterDatesByDayOfWeekOccurrence(
      startDate,
      endDate,
      occurrence,
      dayOfWeek
    ) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const currentDate = new Date();
      let filteredDatesByOccurrence = [];

      while (start <= end) {
        if (
          this.getOccurrenceDayOfMonth(start, occurrence, dayOfWeek) ===
            start.getDate() &&
          start.getDay() === this.getDayOfWeekNumber(dayOfWeek) &&
          start >= currentDate && // Check if current date is greater than or equal to the selected date
          end >= currentDate
        ) {
          const formattedDate = `${String(start.getDate()).padStart(
            2,
            "0"
          )}/${String(start.getMonth() + 1).padStart(
            2,
            "0"
          )}/${start.getFullYear()}`;
          filteredDatesByOccurrence.push(formattedDate);
        }

        // Move to the next day
        start.setDate(start.getDate() + 1);
      }
      return filteredDatesByOccurrence;
    },

    getOccurrenceNumber(occurrence) {
      switch (occurrence.toLowerCase()) {
        case "first":
          return 1;
        case "second":
          return 2;
        case "third":
          return 3;
        case "fourth":
          return 4;
        case "fifth":
          return 5;
        default:
          return 0;
      }
    },

    getOccurrenceDayOfMonth(date, occurrence, dayOfWeek) {
      const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      let dayCounter = 0;

      for (let i = 0; i < 31; i++) {
        const currentDay = new Date(firstDayOfMonth);
        currentDay.setDate(1 + i);

        if (currentDay.getDay() === this.getDayOfWeekNumber(dayOfWeek)) {
          dayCounter++;

          if (dayCounter === this.getOccurrenceNumber(occurrence)) {
            return currentDay.getDate();
          }
        }
      }
      return 0;
    },

    getDayOfWeekNumber(dayOfWeekName) {
      const days = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      const index = days.indexOf(dayOfWeekName);
      return index !== -1 ? index : undefined;
    },

    formatDateNumber(number) {
      if (number <= 9) {
        return "0" + number;
      }
      return `${number}`;
    },
    Cancel() {
      this.selectdatetime = false;
      this.openmultibookingbox = false;
      this.showunavailabledates = false;
      if (
        this.multiBookingFlowActive &&
        (!this.storemultiBookingCartData ||
          this.storemultiBookingCartData.length === 0)
      ) {
        this.multiBookingFlowActive = false;
        this.updateSingleBookingButtonState();
      }
    },

    appendEventData(bookedVenues, selectedValue) {
      this.events = [];

      for (let index = 0; index < bookedVenues.length; index++) {
        const booking = bookedVenues[index];
        if (!booking.StartDate || !booking.EndDate) {
          continue;
        }

        const range = parseBookingDateTimeRange(
          booking.StartDate,
          booking.EndDate
        );
        if (!range) {
          console.warn("Could not parse booking date:", booking.StartDate, booking.EndDate);
          continue;
        }

        this.events.push(
          toCalendarEventInput({
            start: range.start,
            end: range.end,
            title: selectedValue || "Booked",
            extendedProps: { booking },
          })
        );
      }
    },
    getComponentName() {
      return "SearchByDateComponent";
    },
    formatBookingDate(booking) {
      const value = booking?.StartTime;
      if (!value) {
        return "—";
      }
      return value.substring(0, value.length - 5);
    },
    formatAmount(value) {
      const n = parseFloat(value);
      return (Number.isNaN(n) ? 0 : n).toFixed(2);
    },
    formatBookingTimeRange(startValue, endValue) {
      return formatBookingTimeRangeLabel(startValue, endValue);
    },
    findSingleBookingIndex(booking) {
      return (this.storeaddedbookingdetails || []).indexOf(booking);
    },
    findMultiBookingIndex(booking) {
      return (this.storemultiBookingCartData || []).indexOf(booking);
    },
    formatDate(date) {
      const dateParts = date.split(" ");
      const datePart = dateParts[0]
        .split("/")
        .map((part) => this.formatDateNumbers(parseInt(part, 10)));
      const timePart = dateParts[1]
        .split(":")
        .map((part) => this.formatDateNumbers(parseInt(part, 10)));
      return `${datePart[2]}-${datePart[1]}-${datePart[0]} ${timePart[0]}:${timePart[1]}`;
    },
    formatDateNumbers(dateNumber) {
      if (dateNumber < 10) {
        return `0${dateNumber}`;
      }
      return `${dateNumber}`;
    },
    formatDateForAPI(date) {
      if (!date) return null;
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    updateSelectedDate() {
      let selectedDate = null;
      if (this.startDateDaily) {
        selectedDate = this.startDateDaily;
      } else if (this.startDateWeekly) {
        selectedDate = this.startDateWeekly;
      } else if (this.startDateMonthly) {
        selectedDate = this.startDateMonthly;
      }
      
      if (selectedDate) {
        const newDate = new Date(selectedDate);
        if (!this.date || newDate.getTime() !== this.date.getTime()) {
          this.date = newDate;
        }
      }
    },
    onDateSelected(formattedDate) {
      if (formattedDate) {
        // Convert DD/MM/YYYY to Date object
        const [day, month, year] = formattedDate.split('/');
        this.date = new Date(year, month - 1, day);
      }
    },
  },
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.css"></style>
<style src="./Bookingpage.css"></style>

